/**
 * CasesKonfiguration — Cases module configuration.
 *
 * Tabs: SLA-Klassen, Categories, Tags, Templates, Reminders, Reopen,
 *       Vertretungen, OMS, Email Intake, Notifications
 *
 * Note: Zufriedenheit/Survey tab moved to dedicated /zufriedenheit page (v2.120.0)
 *
 * @page /cases/konfiguration
 * @roles admin
 * @i18n casesConfig, sla, vertretung, common
 */
import { useState, useEffect, useCallback } from 'react';
import useTabParam from '../hooks/useTabParam';
import { Shield, Bell, Users, Globe, Plus, Pencil, Trash2, Clock, Save, Send, Download, ArrowLeft, MoreHorizontal, X, Mail, Check, CheckCircle, AlertTriangle, XCircle, RefreshCw, ToggleLeft, ToggleRight, Zap, Tag, FolderOpen, FileText, RotateCcw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { ToggleSwitch } from '../components/ui';
import ConfirmDialog from '../components/ConfirmDialog';

const API = import.meta.env.VITE_API_URL || '';

const TAB_DEFS = [
  { key: 'sla',           labelKey: 'casesConfig.slaTab',           Icon: Shield },
  { key: 'categories',    labelKey: 'casesConfig.categoriesTab',    Icon: FolderOpen },
  { key: 'tags',          labelKey: 'casesConfig.tagsTab',          Icon: Tag },
  { key: 'templates',     labelKey: 'casesConfig.templatesTab',     Icon: FileText },
  { key: 'reminders',     labelKey: 'casesConfig.remindersTab',     Icon: Bell },
  { key: 'reopen',        labelKey: 'casesConfig.reopenTab',        Icon: RotateCcw },
  { key: 'vertretungen',  labelKey: 'casesConfig.vertretungenTab',  Icon: Users },
  { key: 'oms',           labelKey: 'casesConfig.omsTab',           Icon: Globe },
  { key: 'email_intake',  labelKey: 'inboundEmail.config',          Icon: Mail },
  { key: 'autogen',       labelKey: 'autogenWhitelist.tab',         Icon: Shield },
  { key: 'notifications', labelKey: 'stageNotification.tabTitle',   Icon: Zap },
  { key: 'analyse',       labelKey: 'casesConfig.analyseTab',       Icon: Zap },
];

const FARBE_OPTIONS = [
  { value: 'gray', label: 'Gray', cls: 'bg-gray-100 text-[var(--text-secondary,#4B5563)] dark:bg-gray-800 dark:text-[var(--text-tertiary,#6B7280)]' },
  { value: 'violet', label: 'Violet', cls: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400' },
  { value: 'orange', label: 'Orange', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  { value: 'red', label: 'Red', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  { value: 'green', label: 'Green', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  { value: 'blue', label: 'Blue', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
];

function farbeCls(farbe) {
  return FARBE_OPTIONS.find(f => f.value === farbe)?.cls || FARBE_OPTIONS[0].cls;
}

const inp = "w-full px-3 py-2 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500";

/* ── Toast ── */
function Toast({ message, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className="fixed bottom-6 right-6 z-50 px-4 py-2.5 rounded-card bg-surface-elevated border border-line shadow-sm text-sm text-content animate-in fade-in slide-in-from-bottom-4">
      {message}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Escalation Check Button — triggers SLA check synchronously, shows result modal
   ═══════════════════════════════════════════════════════════ */
function EscalationCheckButton({ t, showToast }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const runCheck = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API}/api/admin/sla-check`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Error'); }
      const data = await res.json();
      setResult(data);
    } catch (e) {
      showToast(t('sla.escalationCheckError'));
    }
    setRunning(false);
  };

  const emailLabel = result ? (
    result.email_status === 'disabled' ? t('sla.checkEmailDisabled')
    : result.email_status === 'test' ? t('sla.checkEmailTest')
    : ''
  ) : '';

  return (
    <>
      <div className="bg-surface-elevated rounded-card border border-line">
        <div className="px-5 py-3 border-b border-line">
          <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
            <Zap className="w-4 h-4" /> {t('sla.runEscalationCheck')}
          </h2>
        </div>
        <div className="px-5 py-4 flex items-center justify-between">
          <p className="text-xs text-[var(--text-secondary)] max-w-md">
            {t('sla.empfaengerHint')}
          </p>
          <button onClick={runCheck} disabled={running}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50 whitespace-nowrap">
            <RefreshCw className={`w-3.5 h-3.5 ${running ? 'animate-spin' : ''}`} />
            {running ? t('sla.escalationCheckRunning') : t('sla.runEscalationCheck')}
          </button>
        </div>
      </div>

      {/* Result Modal */}
      {result && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setResult(null)}>
          <div className="bg-surface-elevated rounded-xl border border-line shadow-sm w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-line flex items-center justify-between">
              <h3 className="text-sm font-semibold text-content flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 text-green-600" /> {t('sla.checkResultTitle')}
              </h3>
              <button onClick={() => setResult(null)} className="p-1 rounded hover:bg-[var(--bg-inset)]"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 py-4 space-y-2">
              <ResultRow label={t('sla.checkCasesChecked')} value={result.checked} />
              <ResultRow label={t('sla.checkCasesWarned')} value={result.warned} color={result.warned > 0 ? 'text-amber-600' : ''} />
              <ResultRow label={t('sla.checkCasesEscalated')} value={result.escalated} color={result.escalated > 0 ? 'text-orange-600' : ''} />
              <ResultRow label={t('sla.checkCasesCritical')} value={result.critical} color={result.critical > 0 ? 'text-red-600' : ''} />
              <div className="border-t border-line my-2" />
              <ResultRow label={t('sla.checkNotificationsSent')} value={result.notifications_sent} />
              <ResultRow label={t('sla.checkEmailsSent')} value={result.emails_sent} suffix={emailLabel} />
              <ResultRow label={t('sla.checkUnassigned')} value={result.unassigned_warned} color={result.unassigned_warned > 0 ? 'text-amber-600' : ''} />
            </div>
            <div className="px-5 py-3 border-t border-line flex justify-end">
              <button onClick={() => setResult(null)}
                className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-[var(--bg-inset)] hover:bg-gray-200 transition-colors">
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ResultRow({ label, value, color = '', suffix = '' }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-[var(--text-secondary)]">{label}</span>
      <span className={`font-semibold ${color || 'text-content'}`}>
        {value}{suffix ? <span className="ml-1.5 font-normal text-[var(--text-secondary)]">({suffix})</span> : null}
      </span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Tab 1: SLA Classes (moved from Einstellungen SlaTab)
   ═══════════════════════════════════════════════════════════ */
function SlaTab({ t, showToast }) {
  const [klassen, setKlassen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editKlasse, setEditKlasse] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pauseStages, setPauseStages] = useState([]);
  const [stageLabels, setStageLabels] = useState([]);
  const [configSaving, setConfigSaving] = useState(false);
  const [bulkMap, setBulkMap] = useState({ A: '', B: '', C: '' });
  const [bulkSaving, setBulkSaving] = useState(false);
  const [segCounts, setSegCounts] = useState({ A: 0, B: 0, C: 0 });
  // #1242 — Folge-Antwort-Verhalten (System B)
  const [followup, setFollowup] = useState({ resume: true, mode: 'full', stunden: 24 });
  const [followupSaving, setFollowupSaving] = useState(false);
  const [klOverrides, setKlOverrides] = useState({}); // { [klasseId]: { mode, stunden } }
  const [klOvSaving, setKlOvSaving] = useState(false);
  // #1801 — Cases-Suche: Verhalten für Treffer außerhalb der aktiven Filter (on_click | auto)
  const [outsideMode, setOutsideMode] = useState('on_click');
  const [outsideSaving, setOutsideSaving] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [kRes, cRes, sRes, snRes] = await Promise.all([
        fetch(`${API}/api/sla-klassen/alle`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/einstellungen`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/kunden?limit=9999`, { credentials: 'include' }).then(r => r.json()).catch(() => []),
        fetch(`${API}/api/cases/stage-notifications/config`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ configs: [] })),
      ]);
      const klList = kRes.klassen || [];
      setKlassen(klList);
      // #1242 — Per-Klasse Override-State aus den Klassen-Rows aufbauen
      const ovMap = {};
      for (const k of klList) {
        ovMap[k.id] = {
          mode: k.followup_frist_mode || '',
          stunden: k.followup_stunden == null ? '' : String(k.followup_stunden),
        };
      }
      setKlOverrides(ovMap);
      // Build stage labels from notification config (custom names)
      const configs = snRes.configs || [];
      const labels = configs
        .filter(c => !c.from_stage && c.to_stage)
        .map(c => ({ key: c.to_stage, label: c.stage_label || c.to_stage }))
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      if (labels.length) setStageLabels(labels);
      const cats = cRes.kategorien || {};
      const allSettings = Object.values(cats).flat();
      for (const s of allSettings) {
        if (s.key === 'sla_pause_stages') {
          // #1055: Backend liefert s.value als nativen Array (JSONB auto-parse) ODER String (legacy CSV).
          // Defensive: erst Array-Check, dann JSON-String-Parse, zuletzt CSV-Fallback.
          const v = s.value;
          if (Array.isArray(v)) {
            setPauseStages(v);
          } else if (typeof v === 'string' && v.trim()) {
            try {
              const parsed = JSON.parse(v);
              setPauseStages(Array.isArray(parsed) ? parsed : v.split(',').map(x => x.trim()).filter(Boolean));
            } catch {
              setPauseStages(v.split(',').map(x => x.trim()).filter(Boolean));
            }
          } else {
            setPauseStages([]);
          }
        }
        // #1242 — Folge-Antwort-Verhalten parsen
        if (s.key === 'sla_resume_on_customer_reply') {
          setFollowup(p => ({ ...p, resume: s.value === true || s.value === 'true' }));
        } else if (s.key === 'sla_followup_frist_mode') {
          const m = typeof s.value === 'string' ? s.value : 'full';
          setFollowup(p => ({ ...p, mode: ['full', 'continue', 'followup'].includes(m) ? m : 'full' }));
        } else if (s.key === 'sla_followup_stunden') {
          const n = parseInt(s.value, 10);
          setFollowup(p => ({ ...p, stunden: Number.isFinite(n) && n > 0 ? n : 24 }));
        } else if (s.key === 'cases_search_outside_filter_mode') {
          // #1801 — JSONB-String ('"on_click"') oder nativ; alles außer 'auto' = Default on_click
          setOutsideMode(String(s.value).replace(/"/g, '') === 'auto' ? 'auto' : 'on_click');
        }
      }
      const kunden = Array.isArray(sRes) ? sRes : (sRes.kunden || []);
      const counts = { A: 0, B: 0, C: 0 };
      for (const k of kunden) {
        if (k.segment && counts[k.segment] !== undefined) counts[k.segment]++;
      }
      setSegCounts(counts);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveKlasse = async (data) => {
    setSaving(true);
    try {
      const isNew = !data.id;
      const url = isNew ? `${API}/api/sla-klassen` : `${API}/api/sla-klassen/${data.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Error');
      setEditKlasse(null);
      showToast(t('sla.saved'));
      loadAll();
    } catch (e) { showToast(e.message); }
    setSaving(false);
  };

  const toggleAktiv = async (klasse) => {
    try {
      const res = await fetch(`${API}/api/sla-klassen/${klasse.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ aktiv: !klasse.aktiv }),
      });
      if (res.ok) { showToast(t('sla.saved')); loadAll(); }
    } catch { /* ignore */ }
  };

  const saveConfig = async () => {
    setConfigSaving(true);
    try {
      // #1055: Backend (type='text') wrapt value in JSON.stringify -> wir senden den RAW Array,
      //         damit DB als JSONB-Array landet (nicht als JSONB-String-of-Array).
      await fetch(`${API}/api/einstellungen/sla_pause_stages`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ value: pauseStages }) });
      showToast(t('sla.saved'));
    } catch { showToast('Error'); }
    setConfigSaving(false);
  };

  // #1242 — Globale Folge-Antwort-Konfig speichern (3 system_config-Keys)
  const saveFollowup = async () => {
    setFollowupSaving(true);
    try {
      const put = (key, value) => fetch(`${API}/api/einstellungen/${key}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ value }) });
      await put('sla_resume_on_customer_reply', followup.resume);
      await put('sla_followup_frist_mode', followup.mode);
      await put('sla_followup_stunden', Number(followup.stunden) || 24);
      showToast(t('sla.saved'));
    } catch { showToast('Error'); }
    setFollowupSaving(false);
  };

  // #1801 — Outside-Filter-Modus der kategorisierten Suche speichern
  const saveOutsideMode = async () => {
    setOutsideSaving(true);
    try {
      await fetch(`${API}/api/einstellungen/cases_search_outside_filter_mode`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ value: outsideMode }),
      });
      showToast(t('sla.saved'));
    } catch { showToast('Error'); }
    setOutsideSaving(false);
  };

  // #1242 — Per-Klasse Override speichern (leer = globalen Default verwenden → null)
  const saveKlOverrides = async () => {
    setKlOvSaving(true);
    try {
      for (const k of klassen) {
        const ov = klOverrides[k.id] || {};
        const mode = ['full', 'continue', 'followup'].includes(ov.mode) ? ov.mode : null;
        const sh = parseInt(ov.stunden, 10);
        const stunden = Number.isFinite(sh) && sh > 0 ? sh : null;
        await fetch(`${API}/api/sla-klassen/${k.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ followup_frist_mode: mode, followup_stunden: stunden }),
        });
      }
      showToast(t('sla.saved'));
      loadAll();
    } catch { showToast('Error'); }
    setKlOvSaving(false);
  };

  const bulkAssign = async () => {
    const zuweisungen = [];
    if (bulkMap.A) zuweisungen.push({ segment: 'A', sla_klasse_id: Number(bulkMap.A) });
    if (bulkMap.B) zuweisungen.push({ segment: 'B', sla_klasse_id: Number(bulkMap.B) });
    if (bulkMap.C) zuweisungen.push({ segment: 'C', sla_klasse_id: Number(bulkMap.C) });
    if (!zuweisungen.length) return;
    setBulkSaving(true);
    try {
      const res = await fetch(`${API}/api/kunden/sla-bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ zuweisungen }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error');
      showToast(t('sla.bulkSuccess', { count: data.updated }));
    } catch (e) { showToast(e.message); }
    setBulkSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-48"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>;

  const activeKlassen = klassen.filter(k => k.aktiv);

  return (
    <div className="space-y-6">
      {/* SLA Classes */}
      <div className="bg-surface-elevated rounded-card border border-line">
        <div className="px-5 py-3 border-b border-line flex items-center justify-between">
          <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
            <Shield className="w-4 h-4" /> {t('sla.klassen')}
          </h2>
          <button onClick={() => setEditKlasse({ name: '', label: '', stunden: 24, farbe: 'gray', icon: '', sortierung: 0, eskalation_empfaenger: 'manager' })}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors">
            <Plus className="w-3.5 h-3.5" /> {t('sla.neueKlasse')}
          </button>
        </div>
        <div className="divide-y divide-line">
          {klassen.map(k => (
            <div key={k.id} className={`flex items-center gap-4 px-5 py-3 ${!k.aktiv ? 'opacity-50' : ''}`}>
              <span className={`px-2.5 py-1 rounded text-xs font-semibold ${farbeCls(k.farbe)}`}>
                {k.icon} {k.label}
              </span>
              <span className="text-sm text-content font-medium tabular-nums">{k.stunden}h</span>
              <span className="text-xs text-[var(--text-secondary)] flex-1 truncate">
                <Users className="w-3 h-3 inline mr-1" />
                {k.eskalation_empfaenger || 'manager'}
              </span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${k.aktiv ? 'bg-green-500/15 text-green-500' : 'bg-surface-inset text-content-tertiary'}`}>
                {k.aktiv ? t('sla.aktiv') : t('sla.inaktiv')}
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => setEditKlasse({ ...k })} className="p-1.5 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] hover:text-content transition-colors">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => toggleAktiv(k)} className="p-1.5 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] hover:text-content transition-colors" title={k.aktiv ? t('sla.deaktivieren') : t('sla.aktivieren')}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
          {klassen.length === 0 && (
            <div className="py-8 text-center text-sm text-[var(--text-tertiary)]">{t('sla.noKlassen')}</div>
          )}
        </div>
      </div>

      {/* Edit/Create Modal */}
      {editKlasse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEditKlasse(null)} />
          <div className="relative bg-surface-elevated border border-line rounded-card shadow-elevated w-full max-w-md p-6">
            <h3 className="text-base font-bold text-content mb-4">{editKlasse.id ? t('sla.bearbeiten') : t('sla.erstellen')}</h3>
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-[var(--text-secondary)]">{t('sla.name')}</span>
                <input value={editKlasse.name} onChange={e => setEditKlasse(p => ({ ...p, name: e.target.value }))} className={`mt-1 ${inp}`} placeholder="express" disabled={!!editKlasse.id} />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-[var(--text-secondary)]">{t('sla.label')}</span>
                <input value={editKlasse.label} onChange={e => setEditKlasse(p => ({ ...p, label: e.target.value }))} className={`mt-1 ${inp}`} placeholder="Express" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-[var(--text-secondary)]">{t('sla.stunden')}</span>
                  <input type="number" value={editKlasse.stunden} onChange={e => setEditKlasse(p => ({ ...p, stunden: Number(e.target.value) }))} className={`mt-1 ${inp}`} min="1" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-[var(--text-secondary)]">{t('sla.farbe')}</span>
                  <select value={editKlasse.farbe} onChange={e => setEditKlasse(p => ({ ...p, farbe: e.target.value }))} className={`mt-1 ${inp}`}>
                    {FARBE_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-[var(--text-secondary)]">{t('sla.icon')}</span>
                  <input value={editKlasse.icon || ''} onChange={e => setEditKlasse(p => ({ ...p, icon: e.target.value }))} className={`mt-1 ${inp}`} placeholder="e.g. ⚡" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-[var(--text-secondary)]">{t('sla.sortierung')}</span>
                  <input type="number" value={editKlasse.sortierung || 0} onChange={e => setEditKlasse(p => ({ ...p, sortierung: Number(e.target.value) }))} className={`mt-1 ${inp}`} />
                </label>
              </div>
              <label className="block">
                <span className="text-xs font-medium text-[var(--text-secondary)]">{t('sla.empfaenger')}</span>
                <input value={editKlasse.eskalation_empfaenger || ''} onChange={e => setEditKlasse(p => ({ ...p, eskalation_empfaenger: e.target.value }))} className={`mt-1 ${inp}`} placeholder="manager,admin" />
                <span className="text-[10px] text-[var(--text-tertiary)] mt-0.5 block">{t('sla.empfaengerHint')}</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-line">
              <button onClick={() => setEditKlasse(null)} className="px-4 py-2 rounded-lg text-sm text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover transition-colors">{t('common.cancel')}</button>
              <button onClick={() => saveKlasse(editKlasse)} disabled={saving || !editKlasse.name || !editKlasse.label || !editKlasse.stunden}
                className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
                {saving ? '...' : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SLA Timer-Pause Config */}
      <div className="bg-surface-elevated rounded-card border border-line">
        <div className="px-5 py-3 border-b border-line">
          <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
            <Clock className="w-4 h-4" /> {t('sla.timerConfig')}
          </h2>
          <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">{t('sla.calendarHoursHint')}</p>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <span className="text-xs font-medium text-[var(--text-secondary)]">{t('sla.pauseStages')}</span>
            <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5 mb-2">{t('sla.pauseStagesHint')}</p>
            <div className="grid grid-cols-2 gap-2">
              {(stageLabels.length ? stageLabels : [
                { key: 'response_required', label: t('cases.stage.responseRequired') },
                { key: 'waiting_for_support', label: t('cases.stage.waitingForSupport') },
                { key: 'in_progress', label: t('cases.stage.inProgress') },
                { key: 'clarification_with_recipient', label: t('cases.stage.clarification') },
                { key: 'solution_proposed', label: t('cases.stage.solutionProposed') },
                { key: 'reopened', label: t('cases.stage.reopened') },
                { key: 'closed', label: t('cases.stage.closed') },
              ]).map(st => {
                const checked = pauseStages.includes(st.key);
                return (
                  <label key={st.key} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    checked ? 'border-[#338edc]/40 bg-blue-50/50 dark:bg-blue-500/10 dark:border-blue-500/30' : 'border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-card-hover,#FAFAFE)]'
                  }`}>
                    <input type="checkbox" checked={checked} onChange={() => {
                      setPauseStages(prev => checked ? prev.filter(k => k !== st.key) : [...prev, st.key]);
                    }} className="w-3.5 h-3.5 rounded border-[var(--border-default,#E5E7EB)] text-[#338edc] focus:ring-[#338edc]" />
                    <span className="text-xs text-content">{st.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end">
            <button onClick={saveConfig} disabled={configSaving}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
              <Save className="w-3.5 h-3.5" /> {configSaving ? '...' : t('common.save')}
            </button>
          </div>
        </div>
      </div>

      {/* #1242 — Folge-Antwort-Verhalten (System B) */}
      <div className="bg-surface-elevated rounded-card border border-line">
        <div className="px-5 py-3 border-b border-line">
          <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
            <Clock className="w-4 h-4" /> {t('sla.followupTitle')}
          </h2>
          <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">{t('sla.followupHint')}</p>
        </div>
        <div className="px-5 py-4 space-y-4">
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={followup.resume} onChange={() => setFollowup(p => ({ ...p, resume: !p.resume }))}
              className="w-3.5 h-3.5 rounded border-[var(--border-default,#E5E7EB)] text-[#338edc] focus:ring-[#338edc]" />
            <span className="text-xs font-medium text-content">{t('sla.followupResume')}</span>
          </label>
          <p className="text-[10px] text-[var(--text-tertiary)] -mt-2 ml-6">{t('sla.followupResumeHint')}</p>
          <div className={followup.resume ? '' : 'opacity-50 pointer-events-none'}>
            <span className="text-xs font-medium text-[var(--text-secondary)]">{t('sla.followupMode')}</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {[
                { v: 'full', label: t('sla.followupModeFull') },
                { v: 'continue', label: t('sla.followupModeContinue') },
                { v: 'followup', label: t('sla.followupModeFollowup') },
              ].map(m => (
                <label key={m.v} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                  followup.mode === m.v ? 'border-[#338edc]/40 bg-blue-50/50 dark:bg-blue-500/10 dark:border-blue-500/30' : 'border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-card-hover,#FAFAFE)]'
                }`}>
                  <input type="radio" name="followup-mode" checked={followup.mode === m.v} onChange={() => setFollowup(p => ({ ...p, mode: m.v }))}
                    className="w-3.5 h-3.5 text-[#338edc] focus:ring-[#338edc]" />
                  <span className="text-xs text-content">{m.label}</span>
                </label>
              ))}
            </div>
            {followup.mode === 'followup' && (
              <div className="flex items-center gap-2 mt-3">
                <span className="text-xs text-[var(--text-secondary)]">{t('sla.followupStunden')}</span>
                <input type="number" min="1" value={followup.stunden}
                  onChange={e => setFollowup(p => ({ ...p, stunden: e.target.value }))}
                  className={`w-24 ${inp}`} />
                <span className="text-xs text-[var(--text-tertiary)]">h</span>
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <button onClick={saveFollowup} disabled={followupSaving}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
              <Save className="w-3.5 h-3.5" /> {followupSaving ? '...' : t('common.save')}
            </button>
          </div>

          {/* Per-Klasse Override */}
          {klassen.length > 0 && (
            <div className="pt-4 border-t border-line">
              <span className="text-xs font-medium text-[var(--text-secondary)]">{t('sla.followupPerKlasse')}</span>
              <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5 mb-2">{t('sla.followupPerKlasseHint')}</p>
              <div className="space-y-2">
                {klassen.map(k => {
                  const ov = klOverrides[k.id] || { mode: '', stunden: '' };
                  return (
                    <div key={k.id} className="flex items-center gap-3 flex-wrap">
                      <span className="w-40 text-xs font-medium text-content truncate">{k.icon} {k.label} <span className="text-[var(--text-tertiary)]">({k.stunden}h)</span></span>
                      <select value={ov.mode} onChange={e => setKlOverrides(p => ({ ...p, [k.id]: { ...p[k.id], mode: e.target.value } }))} className={`w-44 ${inp}`}>
                        <option value="">{t('sla.followupModeDefault')}</option>
                        <option value="full">{t('sla.followupModeFull')}</option>
                        <option value="continue">{t('sla.followupModeContinue')}</option>
                        <option value="followup">{t('sla.followupModeFollowup')}</option>
                      </select>
                      {ov.mode === 'followup' && (
                        <input type="number" min="1" placeholder="h" value={ov.stunden}
                          onChange={e => setKlOverrides(p => ({ ...p, [k.id]: { ...p[k.id], stunden: e.target.value } }))}
                          className={`w-20 ${inp}`} />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end mt-3">
                <button onClick={saveKlOverrides} disabled={klOvSaving}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
                  <Save className="w-3.5 h-3.5" /> {klOvSaving ? '...' : t('common.save')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* #1801 — Cases-Suche: Treffer außerhalb der Filter (on_click | auto) */}
      <div className="bg-surface-elevated rounded-card border border-line">
        <div className="px-5 py-3 border-b border-line">
          <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
            <FolderOpen className="w-4 h-4" /> {t('cases.csearch.settingTitle', 'Cases-Suche: Treffer außerhalb der Filter')}
          </h2>
          <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">{t('cases.csearch.settingHint', 'Wie die Suche Treffer anzeigt, die von den aktiven Listen-Filtern ausgeblendet würden.')}</p>
        </div>
        <div className="p-5 flex items-center gap-3 flex-wrap">
          <select value={outsideMode} onChange={e => setOutsideMode(e.target.value)} className={`w-96 max-w-full ${inp}`}>
            <option value="on_click">{t('cases.csearch.settingOnClick', 'on_click — Zeile „+ X weitere Treffer" erst auf Klick aufklappen (Default)')}</option>
            <option value="auto">{t('cases.csearch.settingAuto', 'auto — Abschnitt „Außerhalb deiner Filter" automatisch anzeigen')}</option>
          </select>
          <button onClick={saveOutsideMode} disabled={outsideSaving}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {outsideSaving ? '...' : t('common.save')}
          </button>
        </div>
      </div>

      {/* Bulk Assignment */}
      <div className="bg-surface-elevated rounded-card border border-line">
        <div className="px-5 py-3 border-b border-line">
          <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
            <Users className="w-4 h-4" /> {t('sla.bulkTitle')}
          </h2>
        </div>
        <div className="px-5 py-4 space-y-3">
          {[
            { seg: 'A', label: t('sla.aKunden'), count: segCounts.A },
            { seg: 'B', label: t('sla.bKunden'), count: segCounts.B },
            { seg: 'C', label: t('sla.cKunden'), count: segCounts.C },
          ].map(item => (
            <div key={item.seg} className="flex items-center gap-3">
              <span className="w-28 text-sm font-medium text-content">{item.label} <span className="text-[var(--text-tertiary)]">({item.count})</span></span>
              <select value={bulkMap[item.seg]} onChange={e => setBulkMap(p => ({ ...p, [item.seg]: e.target.value }))} className={`flex-1 ${inp}`}>
                <option value="">—</option>
                {activeKlassen.map(k => <option key={k.id} value={k.id}>{k.icon} {k.label} ({k.stunden}h)</option>)}
              </select>
            </div>
          ))}
          <div className="flex justify-end pt-2">
            <button onClick={bulkAssign} disabled={bulkSaving || (!bulkMap.A && !bulkMap.B && !bulkMap.C)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
              {bulkSaving ? '...' : t('sla.bulkAssign')}
            </button>
          </div>
        </div>
      </div>

      {/* Manual Escalation Check */}
      <EscalationCheckButton t={t} showToast={showToast} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Tab 2: Reminders
   ═══════════════════════════════════════════════════════════ */
function RemindersTab({ t, showToast }) {
  const [settings, setSettings] = useState({
    cases_reminder_hours: 4,
    cases_escalation_hours: 8,
    cases_reminder_notification: true,
    cases_reminder_email: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/einstellungen`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        const cats = d.kategorien || {};
        const all = Object.values(cats).flat();
        const map = {};
        for (const s of all) map[s.key] = s.value;
        setSettings({
          cases_reminder_hours: Number(map.cases_reminder_hours) || 4,
          cases_escalation_hours: Number(map.cases_escalation_hours) || 8,
          cases_reminder_notification: String(map.cases_reminder_notification) === 'true',
          cases_reminder_email: String(map.cases_reminder_email) === 'true',
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const saveSetting = async (key, value) => {
    setSaving(true);
    try {
      const v = typeof value === 'boolean' ? JSON.stringify(value) : String(value);
      await fetch(`${API}/api/einstellungen/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: v }),
      });
      setSettings(prev => ({ ...prev, [key]: value }));
      showToast(t('sla.saved'));
    } catch { showToast('Error'); }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-48"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6">
      <div className="bg-surface-elevated rounded-card border border-line">
        <div className="px-5 py-3 border-b border-line">
          <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
            <Bell className="w-4 h-4" /> {t('casesConfig.reminders.title')}
          </h2>
        </div>
        <div className="px-5 divide-y divide-line">
          {/* Reminder hours */}
          <div className="flex items-center justify-between gap-6 py-4">
            <div>
              <div className="text-sm font-medium text-content">{t('casesConfig.reminders.waitHours')}</div>
              <div className="text-xs text-[var(--text-secondary)] mt-0.5">{t('casesConfig.reminders.waitHours')}</div>
            </div>
            <input type="number" min="1" max="72" value={settings.cases_reminder_hours}
              onChange={e => setSettings(p => ({ ...p, cases_reminder_hours: Number(e.target.value) }))}
              onBlur={() => saveSetting('cases_reminder_hours', settings.cases_reminder_hours)}
              className={`w-24 ${inp}`} />
          </div>

          {/* Escalation hours */}
          <div className="flex items-center justify-between gap-6 py-4">
            <div>
              <div className="text-sm font-medium text-content">{t('casesConfig.reminders.escalationHours')}</div>
            </div>
            <input type="number" min="1" max="168" value={settings.cases_escalation_hours}
              onChange={e => setSettings(p => ({ ...p, cases_escalation_hours: Number(e.target.value) }))}
              onBlur={() => saveSetting('cases_escalation_hours', settings.cases_escalation_hours)}
              className={`w-24 ${inp}`} />
          </div>

          {/* In-app notification toggle */}
          <div className="flex items-center justify-between gap-6 py-4">
            <div>
              <div className="text-sm font-medium text-content">{t('casesConfig.reminders.inApp')}</div>
            </div>
            <ToggleSwitch checked={!!settings.cases_reminder_notification} onChange={() => saveSetting('cases_reminder_notification', !settings.cases_reminder_notification)} disabled={saving} />
          </div>

          {/* Email notification toggle */}
          <div className="flex items-center justify-between gap-6 py-4">
            <div>
              <div className="text-sm font-medium text-content">{t('casesConfig.reminders.email')}</div>
            </div>
            <ToggleSwitch checked={!!settings.cases_reminder_email} onChange={() => saveSetting('cases_reminder_email', !settings.cases_reminder_email)} disabled={saving} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Tab: Auto-Reopen Settings
   ═══════════════════════════════════════════════════════════ */
function ReopenTab({ t, showToast }) {
  const [settings, setSettings] = useState({
    cases_reopen_on_reply: true,
    cases_reopen_ai_filter: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/einstellungen`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        const cats = d.kategorien || {};
        const all = Object.values(cats).flat();
        const map = {};
        for (const s of all) map[s.key] = s.value;
        setSettings({
          cases_reopen_on_reply: String(map.cases_reopen_on_reply ?? 'true') === 'true',
          cases_reopen_ai_filter: String(map.cases_reopen_ai_filter ?? 'false') === 'true',
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const saveSetting = async (key, value) => {
    setSaving(true);
    try {
      const v = typeof value === 'boolean' ? JSON.stringify(value) : String(value);
      await fetch(`${API}/api/einstellungen/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: v }),
      });
      setSettings(prev => ({ ...prev, [key]: value }));
      showToast(t('sla.saved'));
    } catch { showToast('Error'); }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-48"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6">
      <div className="bg-surface-elevated rounded-card border border-line">
        <div className="px-5 py-3 border-b border-line">
          <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
            <RotateCcw className="w-4 h-4" /> {t('casesConfig.reopen.title')}
          </h2>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">{t('casesConfig.reopen.subtitle')}</p>
        </div>
        <div className="px-5 divide-y divide-line">
          {/* Auto-reopen toggle */}
          <div className="flex items-center justify-between gap-6 py-4">
            <div>
              <div className="text-sm font-medium text-content">{t('casesConfig.reopen.enabled')}</div>
              <div className="text-xs text-[var(--text-secondary)] mt-0.5">{t('casesConfig.reopen.enabledDesc')}</div>
            </div>
            <ToggleSwitch checked={!!settings.cases_reopen_on_reply} onChange={() => saveSetting('cases_reopen_on_reply', !settings.cases_reopen_on_reply)} disabled={saving} />
          </div>

          {/* AI gratitude filter toggle */}
          <div className="flex items-center justify-between gap-6 py-4">
            <div>
              <div className="text-sm font-medium text-content">{t('casesConfig.reopen.aiFilter')}</div>
              <div className="text-xs text-[var(--text-secondary)] mt-0.5">{t('casesConfig.reopen.aiFilterDesc')}</div>
            </div>
            <ToggleSwitch checked={!!settings.cases_reopen_ai_filter} onChange={() => saveSetting('cases_reopen_ai_filter', !settings.cases_reopen_ai_filter)} disabled={saving || !settings.cases_reopen_on_reply} />
          </div>
        </div>
      </div>

      {/* Info box */}
      <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg px-4 py-3">
        <p className="text-xs text-primary-700 dark:text-primary-300">{t('casesConfig.reopen.info')}</p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Tab 3: Vertretungen (admin overview)
   ═══════════════════════════════════════════════════════════ */
function VertretungenTab({ t, showToast }) {
  const [list, setList] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'active' | 'expired' | 'planned'
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ user_id: '', vertreter_id: '', permanent: false, gueltig_von: '', gueltig_bis: '', notiz: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, u] = await Promise.all([
        fetch(`${API}/api/cases/vertretungen`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/cases/vertretungen/users`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setList(d.vertretungen || []);
      setUsers(u.users || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm({ user_id: '', vertreter_id: '', permanent: false, gueltig_von: '', gueltig_bis: '', notiz: '' }); setEditId(null); setShowForm(false); };

  const handleSave = async () => {
    if (!form.user_id || !form.vertreter_id) return showToast(t('casesConfig.vertretungen.selectBoth'), 'error');
    if (String(form.user_id) === String(form.vertreter_id)) return showToast(t('casesConfig.vertretungen.noSelfSubstitution'), 'error');
    if (!form.permanent && (!form.gueltig_von || !form.gueltig_bis)) return showToast(t('casesConfig.vertretungen.datesRequired'), 'error');
    setSaving(true);
    try {
      const payload = { ...form, user_id: Number(form.user_id), vertreter_id: Number(form.vertreter_id) };
      if (form.permanent) { payload.gueltig_von = null; payload.gueltig_bis = null; }
      const url = editId ? `${API}/api/cases/vertretungen/${editId}` : `${API}/api/cases/vertretungen`;
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) return showToast(data.error || 'Error', 'error');
      showToast(t('common.saved'));
      resetForm();
      load();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleEdit = (v) => {
    setForm({
      user_id: v.user_id, vertreter_id: v.vertreter_id, permanent: !!v.permanent,
      gueltig_von: v.gueltig_von?.slice(0, 10) || '', gueltig_bis: v.gueltig_bis?.slice(0, 10) || '', notiz: v.notiz || '',
    });
    setEditId(v.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API}/api/cases/vertretungen/${id}`, { method: 'DELETE', credentials: 'include' });
      showToast(t('common.deleted'));
      load();
    } catch { showToast('Error'); }
  };

  if (loading) return <div className="flex items-center justify-center h-48"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>;

  const now = new Date().toISOString().slice(0, 10);
  const getStatus = (v) => {
    if (v.permanent) return 'active';
    if (!v.gueltig_von || !v.gueltig_bis) return 'active';
    if (v.gueltig_bis < now) return 'expired';
    if (v.gueltig_von > now) return 'planned';
    return 'active';
  };
  const filtered = list.filter(v => {
    const s = getStatus(v);
    if (filter === 'active') return s === 'active';
    if (filter === 'expired') return s === 'expired';
    if (filter === 'planned') return s === 'planned';
    return true;
  });

  const userLabel = (uid) => {
    const u = users.find(u => u.id === uid);
    return u ? `${u.vorname || ''} ${u.nachname || ''}`.trim() || u.email : `#${uid}`;
  };

  const statusBadge = (v) => {
    const s = getStatus(v);
    if (s === 'expired') return <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-surface-inset text-content-tertiary">{t('casesConfig.vertretungen.expired')}</span>;
    if (s === 'planned') return <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-blue-500/15 text-blue-500">{t('casesConfig.vertretungen.planned')}</span>;
    return <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-green-500/15 text-green-500">{t('casesConfig.vertretungen.active')}</span>;
  };

  return (
    <div className="space-y-4">
      {/* ── Create/Edit Form ── */}
      {showForm && (
        <div className="bg-surface-elevated rounded-card border border-line p-5 space-y-3">
          <h3 className="text-sm font-semibold text-content">{editId ? t('casesConfig.vertretungen.edit') : t('casesConfig.vertretungen.new')}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('casesConfig.vertretungen.user')}</label>
              <select value={form.user_id} onChange={e => setForm(p => ({ ...p, user_id: e.target.value }))}
                className="w-full mt-1 bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-xs text-content focus:outline-none focus:border-[#338edc]">
                <option value="">{t('vertretung.selectUser')}</option>
                {users.map(u => <option key={u.id} value={u.id}>{`${u.vorname || ''} ${u.nachname || ''}`.trim() || u.email}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('casesConfig.vertretungen.deputy')}</label>
              <select value={form.vertreter_id} onChange={e => setForm(p => ({ ...p, vertreter_id: e.target.value }))}
                className="w-full mt-1 bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-xs text-content focus:outline-none focus:border-[#338edc]">
                <option value="">{t('vertretung.selectUser')}</option>
                {users.filter(u => String(u.id) !== String(form.user_id)).map(u => <option key={u.id} value={u.id}>{`${u.vorname || ''} ${u.nachname || ''}`.trim() || u.email}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-content cursor-pointer">
              <input type="checkbox" checked={form.permanent} onChange={e => setForm(p => ({ ...p, permanent: e.target.checked }))}
                className="rounded border-[var(--border-input)] text-[#338edc] focus:ring-[#338edc]" />
              {t('casesConfig.vertretungen.permanent')}
            </label>
          </div>
          {!form.permanent && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('vertretung.gueltigVon')}</label>
                <input type="date" value={form.gueltig_von} onChange={e => setForm(p => ({ ...p, gueltig_von: e.target.value }))}
                  className="w-full mt-1 bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-xs text-content focus:outline-none focus:border-[#338edc]" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('vertretung.gueltigBis')}</label>
                <input type="date" value={form.gueltig_bis} onChange={e => setForm(p => ({ ...p, gueltig_bis: e.target.value }))}
                  className="w-full mt-1 bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-xs text-content focus:outline-none focus:border-[#338edc]" />
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('casesConfig.vertretungen.note')}</label>
            <input type="text" value={form.notiz} onChange={e => setForm(p => ({ ...p, notiz: e.target.value }))} placeholder={t('casesConfig.vertretungen.notePlaceholder')}
              className="w-full mt-1 bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-xs text-content focus:outline-none focus:border-[#338edc]" />
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50 flex items-center gap-1.5">
              <Save className="w-3.5 h-3.5" /> {saving ? '...' : t('common.save')}
            </button>
            <button onClick={resetForm} className="px-4 py-2 rounded-lg text-xs font-semibold border border-line text-[var(--text-secondary)] hover:bg-surface-hover transition-colors">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* ── List ── */}
      <div className="bg-surface-elevated rounded-card border border-line">
        <div className="px-5 py-3 border-b border-line flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
              <Users className="w-4 h-4" /> {t('casesConfig.vertretungen.title')}
            </h2>
            <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">{t('casesConfig.vertretungen.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Filter pills */}
            <div className="flex gap-1">
              {[
                { key: 'all', label: t('casesConfig.vertretungen.all') },
                { key: 'active', label: t('casesConfig.vertretungen.active') },
                { key: 'planned', label: t('casesConfig.vertretungen.planned') },
                { key: 'expired', label: t('casesConfig.vertretungen.expired') },
              ].map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors ${
                    filter === f.key ? 'bg-[#338edc] text-white' : 'text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover'
                  }`}>{f.label}</button>
              ))}
            </div>
            {!showForm && (
              <button onClick={() => { resetForm(); setShowForm(true); }}
                className="px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors flex items-center gap-1">
                <Plus className="w-3 h-3" /> {t('casesConfig.vertretungen.new')}
              </button>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="py-8 text-center text-sm text-[var(--text-tertiary)]">{t('casesConfig.vertretungen.noEntries')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line">
                <th className="text-left px-5 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('casesConfig.vertretungen.user')}</th>
                <th className="text-left px-5 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('casesConfig.vertretungen.deputy')}</th>
                <th className="text-left px-5 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('casesConfig.vertretungen.period')}</th>
                <th className="text-left px-5 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('casesConfig.vertretungen.note')}</th>
                <th className="text-left px-5 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('casesConfig.vertretungen.status')}</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/50">
              {filtered.map(v => {
                const isExpired = getStatus(v) === 'expired';
                return (
                  <tr key={v.id} className={isExpired ? 'opacity-50' : ''}>
                    <td className="px-5 py-2.5 text-content">{userLabel(v.user_id)}</td>
                    <td className="px-5 py-2.5 text-content">{userLabel(v.vertreter_id)}</td>
                    <td className="px-5 py-2.5 text-content-secondary text-xs">
                      {v.permanent ? t('casesConfig.vertretungen.permanent') : (
                        <>{v.gueltig_von?.slice(0, 10) || '?'} — {v.gueltig_bis?.slice(0, 10) || '?'}</>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-content-secondary text-xs truncate max-w-[160px]">{v.notiz || '–'}</td>
                    <td className="px-5 py-2.5">{statusBadge(v)}</td>
                    <td className="px-3 py-2.5 flex items-center gap-1">
                      <button onClick={() => handleEdit(v)} className="p-1 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] hover:text-content transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(v.id)} className="p-1 rounded-lg hover:bg-surface-hover text-red-400 hover:text-red-300 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Tab 4: OMS Connection
   ═══════════════════════════════════════════════════════════ */
function OmsTab({ t, showToast }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [url, setUrl] = useState('');
  const [db, setDb] = useState('');
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [hasPassword, setHasPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/data-hub/connection/oms_odoo`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (d.url) setUrl(d.url);
        if (d.db) setDb(d.db);
        if (d.user) setUser(d.user);
        setHasPassword(!!d.has_password);
        setStatus(d);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/data-hub/connection/oms_odoo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url, db, user, password: password || undefined }),
      });
      const d = await res.json();
      if (d.ok) {
        showToast(t('casesConfig.oms.saved'));
        setPassword('');
        setHasPassword(true);
      } else {
        showToast(d.error || t('casesConfig.oms.saveFailed'));
      }
    } catch { showToast(t('casesConfig.oms.saveFailed')); }
    setSaving(false);
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API}/api/data-hub/test-connection/oms_odoo`, {
        method: 'POST',
        credentials: 'include',
      });
      const d = await res.json();
      if (d.success) {
        showToast(t('casesConfig.oms.connected'));
        setStatus(prev => ({ ...prev, last_test_ok: true, last_test: new Date().toISOString() }));
      } else {
        showToast(d.error || t('casesConfig.oms.disconnected'));
        setStatus(prev => ({ ...prev, last_test_ok: false }));
      }
    } catch { showToast(t('casesConfig.oms.disconnected')); }
    setTesting(false);
  };

  if (loading) return <div className="flex items-center justify-center h-48"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-4">
      <div className="bg-surface-elevated rounded-card border border-line">
        <div className="px-5 py-3 border-b border-line">
          <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
            <Globe className="w-4 h-4" /> {t('casesConfig.oms.title')}
          </h2>
          <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">{t('casesConfig.oms.subtitle')}</p>
        </div>
        <div className="px-5 divide-y divide-line">
          {/* Connection status */}
          <div className="flex items-center justify-between gap-4 py-4">
            <span className="text-sm font-medium text-content">{t('casesConfig.oms.status')}</span>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${status?.last_test_ok ? 'bg-green-500' : status?.last_test_ok === false ? 'bg-red-500' : 'bg-content-tertiary'}`} />
              <span className="text-sm text-content">
                {status?.last_test_ok ? t('casesConfig.oms.connected') : status?.last_test_ok === false ? t('casesConfig.oms.disconnected') : '—'}
              </span>
            </div>
          </div>

          {/* Last test */}
          {status?.last_test && (
            <div className="flex items-center justify-between gap-4 py-4">
              <span className="text-sm font-medium text-content">{t('casesConfig.oms.lastTest')}</span>
              <span className="text-xs text-content-secondary">{new Date(status.last_test).toLocaleString()}</span>
            </div>
          )}

          {/* Editable form */}
          <div className="py-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">{t('casesConfig.oms.url')}</label>
                <input type="text" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://portal.development.dynaflow.at" className={inp} />
              </div>
              <div>
                <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">{t('casesConfig.oms.database')}</label>
                <input type="text" value={db} onChange={e => setDb(e.target.value)} placeholder="oms_production" className={inp} />
              </div>
              <div>
                <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">{t('casesConfig.oms.username')}</label>
                <input type="text" value={user} onChange={e => setUser(e.target.value)} placeholder="admin" className={inp} />
              </div>
              <div>
                <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">{t('casesConfig.oms.password')}</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                    placeholder={hasPassword ? '••••••••' : t('casesConfig.oms.passwordPlaceholder')} className={inp + ' pr-10'} />
                  <button type="button" onClick={() => setShowPassword(s => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-content p-1">
                    {showPassword ? <X className="w-3.5 h-3.5" /> : <Globe className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="py-4 flex items-center gap-3">
            <button onClick={saveConfig} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
              {saving ? t('casesConfig.oms.saving') : t('casesConfig.oms.save')}
            </button>
            <button onClick={testConnection} disabled={testing}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold border border-line text-content hover:bg-surface-active transition-colors disabled:opacity-50">
              {testing ? t('casesConfig.oms.testing') : t('casesConfig.oms.test')}
            </button>
          </div>
        </div>
      </div>

      {/* Info box */}
      <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/20">
        <AlertTriangle className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-primary-700 dark:text-primary-300">{t('casesConfig.oms.personalHint')}</p>
      </div>

      {/* Stage Mapping */}
      <OmsStageMapping t={t} showToast={showToast} />
    </div>
  );
}

/* ─── OMS Stage-Mapping ─── */
const WESHIP_STAGES = [
  { key: 'response_required', labelKey: 'casesConfig.mapping.responseRequired', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  { key: 'waiting_for_support', labelKey: 'casesConfig.mapping.waitingForSupport', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  { key: 'in_progress', labelKey: 'casesConfig.mapping.inProgress', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  { key: 'clarification_with_recipient', labelKey: 'casesConfig.mapping.clarification', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  { key: 'solution_proposed', labelKey: 'casesConfig.mapping.solutionProposed', color: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400' },
  { key: 'done', labelKey: 'casesConfig.mapping.done', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
];

function OmsStageMapping({ t, showToast }) {
  const { reloadStageMapping } = useLanguage();
  const [mapping, setMapping] = useState({});
  const [availableStages, setAvailableStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/cases/config/oms/stages`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setMapping(d.mapping || {}); setAvailableStages(d.availableStages || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const syncFromOdoo = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/api/cases/config/oms/stages/sync`, { method: 'POST', credentials: 'include' });
      const d = await res.json();
      if (d.ok) {
        setMapping(d.mapping || {});
        setAvailableStages(d.availableStages || []);
        showToast(t('casesConfig.mapping.syncOk'));
      } else {
        showToast(d.error || t('casesConfig.mapping.syncFailed'));
      }
    } catch { showToast(t('casesConfig.mapping.syncFailed')); }
    setSyncing(false);
  };

  const saveMapping = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/cases/config/oms/stages`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mapping }),
      });
      const d = await res.json();
      if (d.ok) { showToast(t('casesConfig.mapping.saved')); reloadStageMapping(); }
      else showToast(d.error || t('casesConfig.mapping.saveFailed'));
    } catch { showToast(t('casesConfig.mapping.saveFailed')); }
    setSaving(false);
  };

  const updateMapping = (weshipKey, odooStageId) => {
    const odooStage = availableStages.find(s => s.id === Number(odooStageId));
    setMapping(prev => ({
      ...prev,
      [weshipKey]: {
        ...(prev[weshipKey] || {}),
        odoo_id: odooStage ? odooStage.id : null,
        odoo_name: odooStage ? odooStage.name : null,
        auto: false,
      },
    }));
  };

  const updateCustomLabel = (weshipKey, label) => {
    setMapping(prev => ({
      ...prev,
      [weshipKey]: { ...(prev[weshipKey] || {}), custom_label: label || null },
    }));
  };

  if (loading) return null;

  return (
    <div className="bg-surface-elevated rounded-card border border-line">
      <div className="px-5 py-3 border-b border-line flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-content">{t('casesConfig.mapping.title')}</h2>
          <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">{t('casesConfig.mapping.subtitle')}</p>
        </div>
        <button onClick={syncFromOdoo} disabled={syncing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-line rounded-lg hover:bg-surface-active transition-colors disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? t('casesConfig.mapping.syncing') : t('casesConfig.mapping.syncBtn')}
        </button>
      </div>
      <div className="px-5 py-4 space-y-2">
        <div className="flex items-center gap-3 text-[10px] font-medium text-content-tertiary uppercase tracking-wider pb-1">
          <span className="w-44 text-center">{t('casesConfig.mapping.colStage')}</span>
          <span className="w-3" />
          <span className="flex-1">{t('casesConfig.mapping.colOdoo')}</span>
          <span className="w-40">{t('casesConfig.mapping.colCustomLabel')}</span>
          <span className="w-16 text-right">{t('common.status')}</span>
        </div>
        {WESHIP_STAGES.map(ws => {
          const entry = mapping[ws.key];
          const isMatched = !!entry?.odoo_id;
          const isAuto = entry?.auto;
          return (
            <div key={ws.key} className="flex items-center gap-3">
              <span className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap w-44 text-center ${ws.color}`}>
                {t(ws.labelKey)}
              </span>
              <span className="text-content-tertiary text-xs">→</span>
              <select value={entry?.odoo_id || ''} onChange={e => updateMapping(ws.key, e.target.value)}
                className="flex-1 px-3 py-1.5 rounded-lg border border-line bg-surface text-sm text-content outline-none focus:border-[#338edc] transition-colors">
                <option value="">— {t('casesConfig.mapping.notMapped')} —</option>
                {availableStages.map(os => (
                  <option key={os.id} value={os.id}>{os.name} (ID: {os.id})</option>
                ))}
              </select>
              <input
                type="text"
                value={entry?.custom_label || ''}
                onChange={e => updateCustomLabel(ws.key, e.target.value)}
                placeholder={t(ws.labelKey)}
                className="w-40 px-3 py-1.5 rounded-lg border border-line bg-surface text-sm text-content outline-none focus:border-[#338edc] transition-colors placeholder:text-content-tertiary"
              />
              <span className="text-[11px] w-16 text-right flex-shrink-0">
                {isMatched && isAuto && <span className="text-green-600 dark:text-green-400">{t('casesConfig.mapping.auto')}</span>}
                {isMatched && !isAuto && <span className="text-[#338edc]">{t('casesConfig.mapping.manual')}</span>}
                {!isMatched && <span className="text-amber-500">{t('casesConfig.mapping.missing')}</span>}
              </span>
            </div>
          );
        })}
      </div>
      <div className="px-5 py-3 border-t border-line flex items-center justify-between">
        <span className="text-[10px] text-content-tertiary">{t('casesConfig.mapping.hint')}</span>
        <button onClick={saveMapping} disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
          <Save className="w-3.5 h-3.5" />
          {saving ? t('casesConfig.mapping.saving') : t('casesConfig.mapping.save')}
        </button>
      </div>
    </div>
  );
}



/* ═══════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════
   EMAIL INTAKE TAB — IMAP-Konfiguration + Status
   ══════════════════════════════════════════════════════════════ */

// #1326: Konto-Status für die Liste. M365 ist test-gated — erst ein grüner Verbindungstest schaltet frei.
// #1340: Authorization-Code-Flow — zusätzlich Autorisierungs-Status (Refresh-Token vorhanden / Re-Auth nötig).
function accountStatusBadge(a, t) {
  if (a.provider_type === 'imap_oauth_m365') {
    if (a.reauth_required) {
      return { label: t('inboundEmail.m365ReauthRequired') || 'Re-Autorisierung nötig', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };
    }
    if (a.verbindung_ok_at) {
      const seit = new Date(a.verbindung_ok_at).toLocaleDateString('de-AT');
      return { label: `${t('inboundEmail.activeSince') || 'Aktiv seit'} ${seit}`, cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' };
    }
    if (a.has_refresh_token) {
      return { label: t('inboundEmail.m365TestNeeded') || 'Autorisiert — Test nötig', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' };
    }
    return { label: t('inboundEmail.m365NotAuthorized') || 'Nicht autorisiert', cls: 'bg-gray-100 text-[var(--text-secondary)] dark:bg-gray-800' };
  }
  return a.aktiv
    ? { label: t('inboundEmail.statusActive') || 'aktiv', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' }
    : { label: t('inboundEmail.statusInactive') || 'inaktiv', cls: 'bg-gray-100 text-[var(--text-secondary)] dark:bg-gray-800' };
}

// #1494 — Letzter erfolgreicher Abruf (auch Leerlauf): Relativzeit + Überfällig-Färbung
// (gelb ab >2× Poll-Intervall, rot ab >4×). intervalSec = imap_polling_interval (Sekunden).
function lastFetchInfo(a, intervalSec) {
  if (!a.last_successful_fetch_at) {
    return { label: 'noch nie', cls: 'text-[var(--text-tertiary)]' };
  }
  const ms = Date.now() - new Date(a.last_successful_fetch_at).getTime();
  const min = Math.round(ms / 60000);
  const uhr = new Date(a.last_successful_fetch_at).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
  const rel = min < 1 ? 'gerade eben' : min < 60 ? `vor ${min} Min` : `um ${uhr}`;
  const iv = (parseInt(intervalSec, 10) || 120) / 60;
  let cls = 'text-[var(--text-secondary)]';
  if (min > iv * 4) cls = 'text-red-600 dark:text-red-400 font-semibold';
  else if (min > iv * 2) cls = 'text-amber-600 dark:text-amber-400 font-semibold';
  return { label: rel, cls, title: `Zuletzt erfolgreich: ${new Date(a.last_successful_fetch_at).toLocaleString('de-AT')}` };
}

// #1324 Phase 1: Mehrkonten (imap_basic). #1325 Phase 2: imap_oauth_m365 (XOAUTH2 Client-Credentials).
const EMPTY_ACCOUNT = {
  name: '', provider_type: 'imap_basic', email_address: '',
  imap_host: '', imap_port: '993', imap_user: '', imap_password: '',
  imap_tls: true, imap_mailbox: 'INBOX', aktiv: true, is_send_default: false,
  oauth_tenant_id: '', oauth_client_id: '', oauth_client_secret: '',
  initial_sync_from: '', // #1355 „Import ab" — datetime-local-Wandzeit (Europe/Vienna), '' = kein Cutoff
};

// #1355 — TIMESTAMPTZ (UTC-ISO aus der API) → datetime-local-Wandzeit "YYYY-MM-DDTHH:mm"
// in Europe/Vienna für das „Import ab"-Feld. Leerer/ungültiger Wert → ''.
function utcIsoToViennaLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = {};
  for (const part of new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Vienna', hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(d)) p[part.type] = part.value;
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

function EmailAccountsCard({ t, showToast }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, {} = form
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [pollIntervalSec, setPollIntervalSec] = useState(120); // #1494 Poll-Intervall für Überfällig-Färbung

  const load = useCallback(async () => {
    try {
      const [res, cfg] = await Promise.all([
        fetch(`${API}/api/inbound-emails/accounts`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/inbound-emails/config`, { credentials: 'include' }).then(r => r.json()).catch(() => ({})),
      ]);
      if (res.ok) setAccounts(res.accounts || []);
      const iv = parseInt(cfg?.config?.imap_polling_interval ?? cfg?.imap_polling_interval, 10);
      if (iv) setPollIntervalSec(iv);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // #1340: OAuth-Callback-Rückkehr (?m365=connected|error) als Toast quittieren und URL säubern.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('m365');
    if (!status) return;
    if (status === 'connected') {
      const email = params.get('email') || '';
      showToast(`${t('inboundEmail.m365Connected') || 'Microsoft 365 autorisiert'}${email ? `: ${email}` : ''}`);
    } else if (status === 'error') {
      const msg = params.get('msg') || '';
      showToast(`${t('inboundEmail.m365AuthFailed') || 'Autorisierung fehlgeschlagen'}${msg ? `: ${msg}` : ''}`);
    }
    params.delete('m365'); params.delete('email'); params.delete('msg');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [showToast, t]);

  // #1340: Autorisierung starten — Full-Page-Redirect zum MS-Login (Top-Level-Navigation nötig).
  const handleAuthorize = () => {
    if (!editing?.id) return;
    window.location.href = `${API}/api/inbound-emails/accounts/${editing.id}/authorize`;
  };

  const openNew = () => { setTestResult(null); setEditing({ ...EMPTY_ACCOUNT }); };
  const openEdit = (a) => {
    setTestResult(null);
    setEditing({
      ...EMPTY_ACCOUNT, ...a,
      imap_port: String(a.imap_port || 993),
      imap_tls: a.imap_tls !== false,
      imap_password: a.has_password ? '••••••••' : '',
      oauth_tenant_id: a.oauth_tenant_id || '',
      oauth_client_id: a.oauth_client_id || '',
      oauth_client_secret: a.has_oauth_secret ? '••••••••' : '',
      initial_sync_from: utcIsoToViennaLocal(a.initial_sync_from), // #1355
    });
  };
  const setF = (k, v) => setEditing(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const isEdit = !!editing.id;
      const url = isEdit ? `${API}/api/inbound-emails/accounts/${editing.id}` : `${API}/api/inbound-emails/accounts`;
      // #1326: M365-Aktivierung ist test-gesteuert — "aktiv" NICHT aus dem Formular senden,
      // sonst würde ein veralteter Toggle-Wert die test-gated Freischaltung überschreiben.
      const payload = { ...editing };
      if (payload.provider_type === 'imap_oauth_m365') delete payload.aktiv;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.ok) { showToast(t('common.saved') || 'Gespeichert'); setEditing(null); load(); }
      else showToast(data.error || 'Fehler');
    } catch (err) { showToast(err.message); }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await fetch(`${API}/api/inbound-emails/accounts/test-connection`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editing),
      });
      setTestResult(await res.json());
      // #1326: Status neu laden — ein grüner Test schaltet ein M365-Konto frei (Badge aktualisiert sich).
      if (editing.id) load();
    } catch (err) { setTestResult({ ok: false, error: err.message }); }
    setTesting(false);
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Konto "${a.name}" löschen?`)) return;
    try {
      const res = await fetch(`${API}/api/inbound-emails/accounts/${a.id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (data.ok) { showToast(t('common.deleted') || 'Gelöscht'); load(); }
      else showToast(data.error || 'Fehler');
    } catch (err) { showToast(err.message); }
  };

  if (loading) return null;

  return (
    <div className="border border-line rounded-card p-6 bg-surface-elevated">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-content flex items-center gap-2">
          <Mail className="w-4 h-4 text-[var(--text-secondary)]" />
          {t('inboundEmail.accountsTitle') || 'Postfächer (Mehrkonten)'}
        </h3>
        {!editing && (
          <button onClick={openNew}
            className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors">
            + {t('inboundEmail.accountAdd') || 'Konto hinzufügen'}
          </button>
        )}
      </div>

      {!editing && (
        accounts.length === 0
          ? <p className="text-sm text-[var(--text-secondary)]">{t('inboundEmail.accountsEmpty') || 'Noch keine Konten konfiguriert.'}</p>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] border-b border-line">
                    <th className="text-left py-2 px-2 font-semibold">Name</th>
                    <th className="text-left py-2 px-2 font-semibold">Provider</th>
                    <th className="text-left py-2 px-2 font-semibold">Host</th>
                    <th className="text-left py-2 px-2 font-semibold">Status</th>
                    <th className="text-left py-2 px-2 font-semibold">Letzter Abruf</th>
                    <th className="text-right py-2 px-2 font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map(a => (
                    <tr key={a.id} className="border-b border-line/50 hover:bg-[var(--bg-card-hover)]">
                      <td className="py-2 px-2 text-content font-medium">
                        {a.name}
                        {a.is_send_default && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">Versand</span>}
                      </td>
                      <td className="py-2 px-2 text-[var(--text-secondary)]">{a.provider_type}</td>
                      <td className="py-2 px-2 text-[var(--text-secondary)] truncate max-w-[180px]">{a.imap_host || '–'}</td>
                      <td className="py-2 px-2">
                        {(() => { const s = accountStatusBadge(a, t); return (
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>
                        ); })()}
                      </td>
                      <td className="py-2 px-2 whitespace-nowrap">
                        {(() => { const f = lastFetchInfo(a, pollIntervalSec); return (
                          <span className={`text-xs ${f.cls}`} title={f.title || ''}>{f.label}</span>
                        ); })()}
                      </td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        <button onClick={() => openEdit(a)} className="text-xs text-primary-600 dark:text-primary-400 hover:underline mr-3">{t('common.edit') || 'Bearbeiten'}</button>
                        <button onClick={() => handleDelete(a)} className="text-xs text-red-500 hover:underline">{t('common.delete') || 'Löschen'}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      )}

      {editing && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Name</label>
              <input className={inp} value={editing.name} onChange={e => setF('name', e.target.value)} placeholder="Help Postfach" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Provider</label>
              <select className={inp} value={editing.provider_type} onChange={e => setF('provider_type', e.target.value)}>
                <option value="imap_basic">IMAP (Basic)</option>
                <option value="imap_oauth_m365">Microsoft 365 (OAuth2 / XOAUTH2)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">E-Mail-Adresse</label>
              <input className={inp} value={editing.email_address} onChange={e => setF('email_address', e.target.value)} placeholder="help@weship.eu" />
            </div>

            {editing.provider_type === 'imap_oauth_m365' ? (
              <>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Postfach (User)</label>
                  <input className={inp} value={editing.imap_user} onChange={e => setF('imap_user', e.target.value)} placeholder="help@weship.eu" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Tenant-ID</label>
                  <input className={inp} value={editing.oauth_tenant_id} onChange={e => setF('oauth_tenant_id', e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Client-ID (App-ID)</label>
                  <input className={inp} value={editing.oauth_client_id} onChange={e => setF('oauth_client_id', e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Client-Secret</label>
                  <input className={inp} type="password" value={editing.oauth_client_secret} onChange={e => setF('oauth_client_secret', e.target.value)} placeholder="••••••••" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Mailbox</label>
                  <input className={inp} value={editing.imap_mailbox} onChange={e => setF('imap_mailbox', e.target.value)} placeholder="INBOX" />
                </div>
                <div className="md:col-span-2">
                  <p className="text-xs text-[var(--text-secondary)]">{t('inboundEmail.m365Hint') || 'Host outlook.office365.com:993 (TLS, XOAUTH2). Voraussetzung: Entra-App mit delegierter Berechtigung IMAP.AccessAsUser.All + offline_access und der hinterlegten Redirect-URI. Nach dem Speichern auf „Autorisieren" klicken — der Postfach-Besitzer meldet sich einmalig bei Microsoft an. Credentials werden AES-verschlüsselt gespeichert und nie geloggt.'}</p>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.imapServer')}</label>
                  <input className={inp} value={editing.imap_host} onChange={e => setF('imap_host', e.target.value)} placeholder="imap.example.com" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.imapPort')}</label>
                  <input className={inp} type="number" value={editing.imap_port} onChange={e => setF('imap_port', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.imapUser')}</label>
                  <input className={inp} value={editing.imap_user} onChange={e => setF('imap_user', e.target.value)} placeholder="support@example.com" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.imapPassword')}</label>
                  <input className={inp} type="password" value={editing.imap_password} onChange={e => setF('imap_password', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Mailbox</label>
                  <input className={inp} value={editing.imap_mailbox} onChange={e => setF('imap_mailbox', e.target.value)} placeholder="INBOX" />
                </div>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-6">
            {editing.provider_type !== 'imap_oauth_m365' && (
              <div className="flex items-center gap-3">
                <ToggleSwitch size="sm" checked={editing.imap_tls !== false} onChange={() => setF('imap_tls', !(editing.imap_tls !== false))} />
                <span className="text-sm text-content">{t('inboundEmail.imapTls')}</span>
              </div>
            )}
            {editing.provider_type === 'imap_oauth_m365' ? (
              // #1340: Authorization-Code-Flow — Autorisieren-Button + Status. Aktivierung weiterhin test-gated.
              <div className="flex items-center gap-2 flex-wrap">
                {editing.reauth_required
                  ? <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">{t('inboundEmail.m365ReauthRequired') || 'Re-Autorisierung nötig'}</span>
                  : editing.verbindung_ok_at
                    ? <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">{t('inboundEmail.m365Confirmed') || '✓ Bestätigt — wird gepollt'}</span>
                    : editing.has_refresh_token
                      ? <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">{t('inboundEmail.m365TestNeeded') || 'Autorisiert — Verbindungstest nötig'}</span>
                      : <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-gray-100 text-[var(--text-secondary)] dark:bg-gray-800">{t('inboundEmail.m365NotAuthorized') || 'Nicht autorisiert'}</span>}
                {editing.oauth_account_email && (
                  <span className="text-xs text-[var(--text-tertiary)]">{editing.oauth_account_email}</span>
                )}
                {editing.id ? (
                  <button type="button" onClick={handleAuthorize}
                    className="px-3 py-1 text-xs font-semibold rounded-lg border border-primary-300 text-primary-700 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
                    {editing.has_refresh_token ? (t('inboundEmail.m365Reauthorize') || 'Neu autorisieren') : (t('inboundEmail.m365Authorize') || 'Autorisieren')}
                  </button>
                ) : (
                  <span className="text-xs text-[var(--text-tertiary)]">{t('inboundEmail.m365AuthorizeHint') || 'Konto zuerst speichern, dann autorisieren.'}</span>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <ToggleSwitch size="sm" checked={editing.aktiv !== false} onChange={() => setF('aktiv', !(editing.aktiv !== false))} />
                <span className="text-sm text-content">{t('inboundEmail.accountActive') || 'Aktiv (wird gepollt)'}</span>
              </div>
            )}
            <div className="flex items-center gap-3">
              <ToggleSwitch size="sm" checked={editing.is_send_default === true} onChange={() => setF('is_send_default', !(editing.is_send_default === true))} />
              <span className="text-sm text-content">{t('inboundEmail.accountSendDefault') || 'Versand-Standard'}</span>
            </div>
          </div>
          {/* #1355 — „Import ab" (initial_sync_from) pro Konto, ALLE Kontotypen, uhrzeit-genau Europe/Vienna. Leer = kein Cutoff. */}
          <div className="border-t border-line pt-3 mt-1">
            <label className="block text-xs font-semibold text-content mb-1">{t('inboundEmail.importFrom') || 'Import ab'}</label>
            <div className="flex items-center gap-2 flex-wrap">
              <input type="datetime-local" className={`${inp} max-w-[230px]`}
                value={editing.initial_sync_from || ''}
                onChange={e => setF('initial_sync_from', e.target.value)} />
              {editing.initial_sync_from && (
                <button type="button" onClick={() => setF('initial_sync_from', '')}
                  className="px-2.5 py-1 text-xs font-semibold rounded-lg border border-line text-content hover:bg-[var(--bg-card-hover)] transition-colors">
                  {t('inboundEmail.importFromClear') || 'Zurücksetzen'}
                </button>
              )}
            </div>
            <p className="text-[11px] text-[var(--text-tertiary)] mt-1">{t('inboundEmail.importFromHint') || 'Es werden nur Mails ab diesem Zeitpunkt importiert (Europe/Vienna). Leer = kein Cutoff.'}</p>
          </div>
          {testResult && (
            <div className={`text-sm px-3 py-2 rounded-lg border ${testResult.ok
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400'
              : 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400'}`}>
              {testResult.ok
                ? <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4" />{t('inboundEmail.connectionSuccess')} — {testResult.messages ?? 0} {t('inboundEmail.messagesInMailbox') || 'messages'}{testResult.unseen > 0 ? ` (${testResult.unseen} unread)` : ''}</span>
                : <span className="flex items-center gap-1.5"><XCircle className="w-4 h-4" />{t('inboundEmail.connectionFailed')}: {testResult.error}</span>}
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <button onClick={handleTest} disabled={testing}
              className="px-4 py-2 text-sm font-semibold rounded-lg border border-line text-content hover:bg-[var(--bg-card-hover)] transition-colors disabled:opacity-50">
              {testing ? '...' : t('inboundEmail.testConnection')}
            </button>
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-1.5">
              <Save className="w-3.5 h-3.5" />{saving ? '...' : (t('common.save') || 'Speichern')}
            </button>
            <button onClick={() => { setEditing(null); setTestResult(null); }}
              className="px-4 py-2 text-sm font-semibold rounded-lg border border-line text-content hover:bg-[var(--bg-card-hover)] transition-colors">
              {t('common.cancel') || 'Abbrechen'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── #1330 Phase 4b: Inbound-Regel-Verwaltung (UI auf Basis der #1329-Engine) ──
const EMPTY_RULE = {
  name: '', enabled: true, priority: 100,
  conditions: { received_address: '', sender_or_domain: '', subject_contains: '' },
  actions: { tag: '', category: '', assignee_user_id: '', status: '', priority: '' },
};
const RULE_STAGE_OPTIONS = [
  { v: 'new', l: 'Response Required' },
  { v: 'waiting_for_support', l: 'Waiting for Support' },
  { v: 'in_progress', l: 'In Progress' },
  { v: 'clarification_with_recipient', l: 'Clarification with recipient' },
  { v: 'resolved', l: 'Solution Proposed' },
  { v: 'closed', l: 'Done' },
];
const RULE_PRIORITY_OPTIONS = ['low', 'medium', 'high'];

function EmailRulesCard({ t, showToast }) {
  const [rules, setRules] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, object = form
  const [saving, setSaving] = useState(false);

  const loadRules = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/inbound-emails/rules`, { credentials: 'include' }).then(r => r.json());
      if (res.ok) setRules(res.rules || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadRules();
    fetch(`${API}/api/inbound-emails/accounts`, { credentials: 'include' }).then(r => r.json()).then(d => { if (d.ok) setAccounts(d.accounts || []); }).catch(() => {});
    fetch(`${API}/api/cases/categories`, { credentials: 'include' }).then(r => r.json()).then(d => setCategories(d.categories || [])).catch(() => {});
    fetch(`${API}/api/cases/tags`, { credentials: 'include' }).then(r => r.json()).then(d => setTags(d.tags || [])).catch(() => {});
    fetch(`${API}/api/users`, { credentials: 'include' }).then(r => r.json()).then(d => setUsers((d.users || d || []).filter(u => u.aktiv !== false))).catch(() => {});
  }, [loadRules]);

  const userLabel = (u) => `${u.vorname || ''} ${u.nachname || ''}`.trim() || u.email || `#${u.id}`;
  const tagName = (v) => { const x = tags.find(tg => String(tg.id) === String(v)); return x ? x.name : v; };
  const catName = (v) => { const x = categories.find(c => String(c.id) === String(v)); return x ? x.name : v; };
  const userName = (v) => { const x = users.find(u => String(u.id) === String(v)); return x ? userLabel(x) : v; };
  const stageLabel = (v) => { const x = RULE_STAGE_OPTIONS.find(s => s.v === v); return x ? x.l : v; };

  const openNew = () => setEditing({ ...EMPTY_RULE, priority: rules.length ? Math.max(...rules.map(r => r.priority || 0)) + 10 : 100 });
  const openEdit = (r) => setEditing({
    id: r.id, name: r.name || '', enabled: r.enabled !== false, priority: r.priority ?? 100,
    conditions: { received_address: '', sender_or_domain: '', subject_contains: '', ...(r.conditions || {}) },
    actions: { tag: '', category: '', assignee_user_id: '', status: '', priority: '', ...(r.actions || {}) },
  });
  const setF = (k, v) => setEditing(p => ({ ...p, [k]: v }));
  const setCond = (k, v) => setEditing(p => ({ ...p, conditions: { ...p.conditions, [k]: v } }));
  const setAct = (k, v) => setEditing(p => ({ ...p, actions: { ...p.actions, [k]: v } }));

  const handleSave = async () => {
    if (!editing.name.trim()) { showToast(t('inboundEmail.rulesNameRequired') || 'Name erforderlich'); return; }
    setSaving(true);
    try {
      const isEdit = !!editing.id;
      const url = isEdit ? `${API}/api/inbound-emails/rules/${editing.id}` : `${API}/api/inbound-emails/rules`;
      const payload = {
        name: editing.name.trim(), enabled: !!editing.enabled, priority: parseInt(editing.priority) || 100,
        conditions: editing.conditions, actions: editing.actions,
      };
      const res = await fetch(url, { method: isEdit ? 'PUT' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.ok) { showToast(t('common.saved') || 'Gespeichert'); setEditing(null); loadRules(); }
      else showToast(data.error || 'Fehler');
    } catch (err) { showToast(err.message); }
    setSaving(false);
  };

  const toggleEnabled = async (r) => {
    try {
      await fetch(`${API}/api/inbound-emails/rules/${r.id}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: !r.enabled }) });
      loadRules();
    } catch (err) { showToast(err.message); }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`${t('inboundEmail.rulesDeleteConfirm') || 'Regel löschen?'} "${r.name}"`)) return;
    try {
      const res = await fetch(`${API}/api/inbound-emails/rules/${r.id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (data.ok) { showToast(t('common.deleted') || 'Gelöscht'); loadRules(); }
      else showToast(data.error || 'Fehler');
    } catch (err) { showToast(err.message); }
  };

  // Reihenfolge: Hoch/Runter → renummeriert priority (10/20/30…), robust auch bei Gleichstand.
  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= rules.length) return;
    const arr = [...rules];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    try {
      await Promise.all(arr.map((r, i) => {
        const newPrio = (i + 1) * 10;
        if (r.priority === newPrio) return null;
        return fetch(`${API}/api/inbound-emails/rules/${r.id}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ priority: newPrio }) });
      }).filter(Boolean));
      loadRules();
    } catch (err) { showToast(err.message); }
  };

  const condSummary = (c = {}) => {
    const parts = [];
    if (c.received_address) parts.push(`${t('inboundEmail.rulesCondReceived') || 'Empfangsadresse'}=${c.received_address}`);
    if (c.sender_or_domain) parts.push(`${t('inboundEmail.rulesCondSender') || 'Absender'}=${c.sender_or_domain}`);
    if (c.subject_contains) parts.push(`${t('inboundEmail.rulesCondSubject') || 'Betreff'}~"${c.subject_contains}"`);
    return parts.length ? parts.join(' & ') : (t('inboundEmail.rulesCondNone') || 'keine Bedingung');
  };
  const actSummary = (a = {}) => {
    const parts = [];
    if (a.tag) parts.push(`#${tagName(a.tag)}`);
    if (a.category) parts.push(catName(a.category));
    if (a.assignee_user_id) parts.push(`@${userName(a.assignee_user_id)}`);
    if (a.status) parts.push(stageLabel(a.status));
    if (a.priority) parts.push(a.priority);
    return parts.length ? parts.join(', ') : '—';
  };

  if (loading) return null;

  return (
    <div className="border border-line rounded-card p-6 bg-surface-elevated">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-base font-semibold text-content flex items-center gap-2">
          <Zap className="w-4 h-4 text-[var(--text-secondary)]" />
          {t('inboundEmail.rulesTitle') || 'Inbound-Regeln'}
        </h3>
        {!editing && (
          <button onClick={openNew}
            className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors">
            + {t('inboundEmail.rulesAdd') || 'Regel hinzufügen'}
          </button>
        )}
      </div>
      <p className="text-xs text-[var(--text-secondary)] mb-4">{t('inboundEmail.rulesHint') || 'Regeln werden nach Priorität ausgewertet — die erste passende Regel gewinnt.'}</p>

      {!editing && (
        rules.length === 0
          ? <p className="text-sm text-[var(--text-secondary)]">{t('inboundEmail.rulesEmpty') || 'Noch keine Regeln konfiguriert.'}</p>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] border-b border-line">
                    <th className="text-left py-2 px-2 font-semibold w-14">{t('inboundEmail.rulesOrder') || 'Reihenfolge'}</th>
                    <th className="text-left py-2 px-2 font-semibold">{t('inboundEmail.rulesName') || 'Name'}</th>
                    <th className="text-left py-2 px-2 font-semibold">{t('inboundEmail.rulesSummary') || 'Bedingungen → Aktionen'}</th>
                    <th className="text-left py-2 px-2 font-semibold w-20">{t('inboundEmail.rulesActive') || 'Aktiv'}</th>
                    <th className="text-right py-2 px-2 font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r, idx) => (
                    <tr key={r.id} className="border-b border-line/50 hover:bg-[var(--bg-card-hover)]">
                      <td className="py-2 px-2 whitespace-nowrap">
                        <button onClick={() => move(idx, -1)} disabled={idx === 0}
                          title={t('inboundEmail.rulesMoveUp') || 'Nach oben'}
                          className="text-[var(--text-tertiary)] hover:text-primary-600 disabled:opacity-30 px-1">▲</button>
                        <button onClick={() => move(idx, 1)} disabled={idx === rules.length - 1}
                          title={t('inboundEmail.rulesMoveDown') || 'Nach unten'}
                          className="text-[var(--text-tertiary)] hover:text-primary-600 disabled:opacity-30 px-1">▼</button>
                      </td>
                      <td className="py-2 px-2 text-content font-medium">{r.name}</td>
                      <td className="py-2 px-2 text-[var(--text-secondary)]">
                        <span>{condSummary(r.conditions)}</span>
                        <span className="mx-1.5 text-[var(--text-tertiary)]">→</span>
                        <span className="text-content">{actSummary(r.actions)}</span>
                      </td>
                      <td className="py-2 px-2">
                        <ToggleSwitch size="sm" checked={r.enabled !== false} onChange={() => toggleEnabled(r)} />
                      </td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        <button onClick={() => openEdit(r)} className="text-xs text-primary-600 dark:text-primary-400 hover:underline mr-3">{t('common.edit') || 'Bearbeiten'}</button>
                        <button onClick={() => handleDelete(r)} className="text-xs text-red-500 hover:underline">{t('common.delete') || 'Löschen'}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      )}

      {editing && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.rulesName') || 'Name'}</label>
              <input className={inp} value={editing.name} onChange={e => setF('name', e.target.value)} placeholder={t('inboundEmail.rulesNamePlaceholder') || 'z. B. Retouren → Team Logistik'} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.rulesPriority') || 'Priorität (Reihenfolge)'}</label>
              <input type="number" className={inp} value={editing.priority} onChange={e => setF('priority', e.target.value)} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <ToggleSwitch size="sm" checked={!!editing.enabled} onChange={() => setF('enabled', !editing.enabled)} />
            <span className="text-sm text-content">{t('inboundEmail.rulesActive') || 'Aktiv'}</span>
          </div>

          {/* Bedingungen (UND) */}
          <div className="border border-line rounded-lg p-4">
            <p className="text-xs font-semibold text-content mb-3">{t('inboundEmail.rulesConditions') || 'Bedingungen (alle gesetzten werden UND-verknüpft)'}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.rulesCondReceived') || 'Empfangsadresse'}</label>
                <input className={inp} list="rule-received-addresses" value={editing.conditions.received_address}
                  onChange={e => setCond('received_address', e.target.value)} placeholder={t('inboundEmail.rulesAnyPlaceholder') || '(beliebig)'} />
                <datalist id="rule-received-addresses">
                  {accounts.map(a => a.email_address ? <option key={a.id} value={a.email_address} /> : null)}
                </datalist>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.rulesCondSender') || 'Absender / Absender-Domain'}</label>
                <input className={inp} value={editing.conditions.sender_or_domain}
                  onChange={e => setCond('sender_or_domain', e.target.value)} placeholder="kunde@firma.de | firma.de" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.rulesCondSubject') || 'Betreff enthält'}</label>
                <input className={inp} value={editing.conditions.subject_contains}
                  onChange={e => setCond('subject_contains', e.target.value)} placeholder={t('inboundEmail.rulesAnyPlaceholder') || '(beliebig)'} />
              </div>
            </div>
          </div>

          {/* Aktionen */}
          <div className="border border-line rounded-lg p-4">
            <p className="text-xs font-semibold text-content mb-3">{t('inboundEmail.rulesActions') || 'Aktionen (alle optional)'}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.rulesActionTag') || 'Tag'}</label>
                <select className={inp} value={editing.actions.tag} onChange={e => setAct('tag', e.target.value)}>
                  <option value="">{t('inboundEmail.rulesNoneOption') || '(keine)'}</option>
                  {tags.map(tg => <option key={tg.id} value={tg.id}>{tg.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.rulesActionCategory') || 'Kategorie'}</label>
                <select className={inp} value={editing.actions.category} onChange={e => setAct('category', e.target.value)}>
                  <option value="">{t('inboundEmail.rulesNoneOption') || '(keine)'}</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.rulesActionAssignee') || 'Mitarbeiter zuweisen'}</label>
                <select className={inp} value={editing.actions.assignee_user_id} onChange={e => setAct('assignee_user_id', e.target.value)}>
                  <option value="">{t('inboundEmail.rulesNoneOption') || '(keine)'}</option>
                  {users.filter(u => !['portal_kunde', 'portal_mitarbeiter'].includes(u.rolle)).map(u => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.rulesActionStatus') || 'Status'}</label>
                <select className={inp} value={editing.actions.status} onChange={e => setAct('status', e.target.value)}>
                  <option value="">{t('inboundEmail.rulesNoneOption') || '(keine)'}</option>
                  {RULE_STAGE_OPTIONS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.rulesActionPriority') || 'Priorität'}</label>
                <select className={inp} value={editing.actions.priority} onChange={e => setAct('priority', e.target.value)}>
                  <option value="">{t('inboundEmail.rulesNoneOption') || '(keine)'}</option>
                  {RULE_PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-1.5">
              <Save className="w-3.5 h-3.5" />{saving ? '...' : (t('common.save') || 'Speichern')}
            </button>
            <button onClick={() => setEditing(null)}
              className="px-4 py-2 text-sm font-semibold rounded-lg border border-line text-content hover:bg-[var(--bg-card-hover)] transition-colors">
              {t('common.cancel') || 'Abbrechen'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function EmailIntakeTab({ t, showToast }) {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentEmails, setRecentEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState(null);

  const loadConfig = useCallback(async () => {
    try {
      const [cfgRes, statsRes, emailsRes] = await Promise.all([
        fetch(`${API}/api/inbound-emails/config`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/inbound-emails/stats`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/inbound-emails?limit=10&page=1`, { credentials: 'include' }).then(r => r.json()),
      ]);
      if (cfgRes.ok) setConfig(cfgRes.config);
      if (statsRes.ok) setStats(statsRes.stats);
      if (emailsRes.ok) setRecentEmails(emailsRes.emails || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/inbound-emails/config`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      const data = await res.json();
      if (data.ok) showToast(t('common.saved') || 'Saved');
      else showToast(data.error || 'Error');
    } catch (err) { showToast(err.message); }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API}/api/inbound-emails/test-connection`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      const data = await res.json();
      setTestResult(data);
    } catch (err) { setTestResult({ ok: false, error: err.message }); }
    setTesting(false);
  };

  const handlePollNow = async () => {
    if (!config?.imap_host || !config?.imap_user) {
      showToast(t('inboundEmail.pollNoConfig'));
      return;
    }
    setPolling(true);
    setPollResult(null);
    try {
      const res = await fetch(`${API}/api/inbound-emails/poll-now`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (data.ok) {
        setPollResult(data);
        loadConfig(); // refresh stats + recent emails
      } else {
        showToast(data.error || 'Error');
      }
    } catch (err) { showToast(err.message); }
    setPolling(false);
  };

  const set = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  if (loading) return <div className="text-sm text-[var(--text-secondary)]">Loading...</div>;
  if (!config) return <div className="text-sm text-red-500">Config konnte nicht geladen werden</div>;

  const STATUS_CLS = {
    processed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    pending:   'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    failed:    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    ignored:   'bg-gray-100 text-[var(--text-secondary,#4B5563)] dark:bg-gray-800 dark:text-[var(--text-tertiary,#6B7280)]',
  };

  return (
    <div className="space-y-6">
      {/* ── #1324 Mehrkonten-Verwaltung ── */}
      <EmailAccountsCard t={t} showToast={showToast} />

      {/* ── #1330 Inbound-Regel-Verwaltung (Engine = #1329) ── */}
      <EmailRulesCard t={t} showToast={showToast} />

      {/* ── IMAP Connection ── */}
      <div className="border border-line rounded-card p-6 bg-surface-elevated">
        <h3 className="text-base font-semibold text-content mb-4 flex items-center gap-2">
          <Mail className="w-4 h-4 text-[var(--text-secondary)]" />
          {t('inboundEmail.imapConnection')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.imapServer')}</label>
            <input className={inp} value={config.imap_host || ''} onChange={e => set('imap_host', e.target.value)} placeholder="imap.example.com" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.imapPort')}</label>
            <input className={inp} type="number" value={config.imap_port || '993'} onChange={e => set('imap_port', e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.imapUser')}</label>
            <input className={inp} value={config.imap_user || ''} onChange={e => set('imap_user', e.target.value)} placeholder="support@example.com" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.imapPassword')}</label>
            <input className={inp} type="password" value={config.imap_password || ''} onChange={e => set('imap_password', e.target.value)} />
          </div>
          <div className="flex items-center gap-3 pt-5">
            <ToggleSwitch size="sm" checked={String(config.imap_tls) === 'true'} onChange={() => set('imap_tls', String(config.imap_tls) === 'true' ? 'false' : 'true')} />
            <span className="text-sm text-content">{t('inboundEmail.imapTls')}</span>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={handleTest} disabled={testing}
            className="px-4 py-2 text-sm font-semibold rounded-lg border border-line text-content hover:bg-[var(--bg-card-hover)] transition-colors disabled:opacity-50"
          >
            {testing ? '...' : t('inboundEmail.testConnection')}
          </button>
          <button onClick={handlePollNow} disabled={polling || !config?.imap_host || !config?.imap_user}
            className="px-4 py-2 text-sm font-semibold rounded-lg border border-line text-content hover:bg-[var(--bg-card-hover)] transition-colors disabled:opacity-50 flex items-center gap-1.5"
          >
            <Download className={`w-3.5 h-3.5 ${polling ? 'animate-spin' : ''}`} />
            {polling ? t('inboundEmail.polling') : t('inboundEmail.pollNow')}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-1.5"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? '...' : (t('common.save') || 'Save')}
          </button>
        </div>
        {testResult && (
          <div className={`mt-3 text-sm px-3 py-2 rounded-lg border ${testResult.ok
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400'
            : 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400'
          }`}>
            {testResult.ok
              ? <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4" />{t('inboundEmail.connectionSuccess')} — {testResult.messages ?? 0} {t('inboundEmail.messagesInMailbox') || 'messages'}{testResult.unseen > 0 ? ` (${testResult.unseen} unread)` : ''}</span>
              : <span className="flex items-center gap-1.5"><XCircle className="w-4 h-4" />{t('inboundEmail.connectionFailed')}: {testResult.error}</span>
            }
          </div>
        )}
        {pollResult && (
          <div className="mt-3 text-sm px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400">
            <span className="flex items-center gap-1.5">
              <CheckCircle className="w-4 h-4" />
              {t('inboundEmail.pollResult').replace('{fetched}', pollResult.fetched).replace('{processed}', pollResult.processed)}
            </span>
          </div>
        )}
      </div>

      {/* ── Outgoing SMTP ── */}
      <div className="border border-line rounded-card p-6 bg-surface-elevated">
        <h3 className="text-base font-semibold text-content mb-4 flex items-center gap-2">
          <Send className="w-4 h-4 text-[var(--text-secondary)]" />
          {t('inboundEmail.smtpTitle')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.smtpHost')}</label>
            <input className={inp} value={config.cases_smtp_host || ''} onChange={e => set('cases_smtp_host', e.target.value)} placeholder="smtp.example.com" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.smtpPort')}</label>
            <input className={inp} type="number" value={config.cases_smtp_port || '587'} onChange={e => set('cases_smtp_port', e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.smtpUser')}</label>
            <input className={inp} value={config.cases_smtp_user || ''} onChange={e => set('cases_smtp_user', e.target.value)} placeholder="support@example.com" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.smtpPassword')}</label>
            <input className={inp} type="password" value={config.cases_smtp_password || ''} onChange={e => set('cases_smtp_password', e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.smtpFromName')}</label>
            <input className={inp} value={config.cases_smtp_from_name || ''} onChange={e => set('cases_smtp_from_name', e.target.value)} placeholder="WeShip Support" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.smtpFromEmail')}</label>
            <input className={inp} value={config.cases_smtp_from_email || ''} onChange={e => set('cases_smtp_from_email', e.target.value)} placeholder="help@example.com" />
          </div>
          <div className="flex items-center gap-3 pt-5">
            <ToggleSwitch size="sm" checked={String(config.cases_smtp_tls) === 'true'} onChange={() => set('cases_smtp_tls', String(config.cases_smtp_tls) === 'true' ? 'false' : 'true')} />
            <span className="text-sm text-content">{t('inboundEmail.smtpTls')}</span>
          </div>
        </div>
        <p className="text-xs text-[var(--text-secondary)] mt-4">{t('inboundEmail.smtpHint')}</p>
        <p className="text-xs text-[var(--text-secondary)] mt-1">{t('inboundEmail.smtpFallback')}</p>
      </div>

      {/* ── Processing Settings ── */}
      <div className="border border-line rounded-card p-6 bg-surface-elevated">
        <h3 className="text-base font-semibold text-content mb-4">{t('casesConfig.settings') || 'Settings'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.pollingInterval')}</label>
            <select className={inp} value={config.imap_polling_interval || '120'} onChange={e => set('imap_polling_interval', e.target.value)}>
              <option value="60">60s</option>
              <option value="120">120s</option>
              <option value="300">5min</option>
              <option value="600">10min</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.defaultPriority')}</label>
            <select className={inp} value={config.inbound_email_default_priority || 'medium'} onChange={e => set('inbound_email_default_priority', e.target.value)}>
              <option value="low">{t('cases.priority')}: Low</option>
              <option value="medium">{t('cases.priority')}: Medium</option>
              <option value="high">{t('cases.priority')}: High</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('inboundEmail.defaultStage')}</label>
            <select className={inp} value={config.inbound_email_default_stage || 'waiting_for_support'} onChange={e => set('inbound_email_default_stage', e.target.value)}>
              <option value="new">Response Required</option>
              <option value="waiting_for_support">Waiting for Support</option>
              <option value="in_progress">In Progress</option>
              <option value="clarification_with_recipient">Clarification with recipient</option>
              <option value="resolved">Solution Proposed</option>
              <option value="closed">Done</option>
            </select>
          </div>
          <div className="flex items-center gap-3 pt-5">
            <ToggleSwitch size="sm" checked={String(config.inbound_email_auto_reply) === 'true'} onChange={() => set('inbound_email_auto_reply', String(config.inbound_email_auto_reply) === 'true' ? 'false' : 'true')} />
            <span className="text-sm text-content">{t('inboundEmail.autoReply')}</span>
          </div>
        </div>

        {/* Enable Polling Toggle — prominent */}
        <div className="mt-6 p-4 rounded-lg border border-line bg-surface flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-content">{t('inboundEmail.enablePolling')}</p>
            {(!config.imap_host || !config.imap_user) && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {t('inboundEmail.warningNoConfig')}
              </p>
            )}
          </div>
          <ToggleSwitch size="lg" checked={String(config.imap_enabled) === 'true'} onChange={() => set('imap_enabled', String(config.imap_enabled) === 'true' ? 'false' : 'true')} />
        </div>
      </div>

      {/* ── Status & Recent Emails ── */}
      <div className="border border-line rounded-card p-6 bg-surface-elevated">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-content">Status</h3>
          <button onClick={loadConfig} className="text-xs text-[var(--text-secondary)] hover:text-content flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            {[
              { label: t('inboundEmail.lastCheck'), value: stats.last_poll ? new Date(stats.last_poll).toLocaleString('de') : '–' },
              { label: t('inboundEmail.emailsToday'), value: stats.today || 0 },
              { label: t('inboundEmail.newCases'), value: stats.new_cases_today || 0 },
              { label: t('inboundEmail.caseUpdates'), value: stats.updates_today || 0 },
            ].map((kpi, i) => (
              <div key={i} className="text-center">
                <p className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">{kpi.label}</p>
                <p className="text-xl font-bold text-content mt-1">{kpi.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Recent emails mini-table */}
        {recentEmails.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] border-b border-line">
                  <th className="text-left py-2 px-2 font-semibold">{t('common.date') || 'Date'}</th>
                  <th className="text-left py-2 px-2 font-semibold">{t('common.from') || 'From'}</th>
                  <th className="text-left py-2 px-2 font-semibold">{t('common.subject') || 'Subject'}</th>
                  <th className="text-left py-2 px-2 font-semibold">Case</th>
                  <th className="text-left py-2 px-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentEmails.map(em => (
                  <tr key={em.id} className="border-b border-line/50 hover:bg-[var(--bg-card-hover)]">
                    <td className="py-2 px-2 text-[var(--text-tertiary)] whitespace-nowrap">
                      {new Date(em.received_at).toLocaleString('de', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="py-2 px-2 text-content truncate max-w-[140px]">{em.from_name || em.from_address}</td>
                    <td className="py-2 px-2 text-content truncate max-w-[200px]">{em.subject || '–'}</td>
                    <td className="py-2 px-2">
                      {em.case_id ? <span className="text-primary-600 dark:text-primary-400 font-medium">#{em.case_id}</span> : '–'}
                    </td>
                    <td className="py-2 px-2">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_CLS[em.processing_status] || STATUS_CLS.pending}`}>
                        {em.processing_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Tab 7: Stage Notifications — Matrix Layout
   ═══════════════════════════════════════════════════════════ */
function StageNotificationTab({ t, showToast }) {
  const [configs, setConfigs] = useState([]);
  const [globalEnabled, setGlobalEnabled] = useState(true);
  const [onlyEmailCases, setOnlyEmailCases] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [cfgRes, tplRes] = await Promise.all([
        fetch(`${API}/api/cases/stage-notifications/config`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/email/templates`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ templates: [] })),
      ]);
      if (cfgRes.ok) {
        setConfigs(cfgRes.configs || []);
        setGlobalEnabled(cfgRes.globalEnabled !== false);
        setOnlyEmailCases(cfgRes.onlyEmailCases === true);
      }
      setTemplates(tplRes.templates || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const updateGlobalSetting = async (field, value) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/cases/stage-notifications/settings`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      const data = await res.json();
      if (data.ok) {
        if (field === 'enabled') setGlobalEnabled(value);
        if (field === 'onlyEmailCases') setOnlyEmailCases(value);
        showToast(t('common.saved') || 'Saved');
      } else showToast(data.error || 'Error');
    } catch (err) { showToast(err.message); }
    setSaving(false);
  };

  const updateConfig = async (cfg, updates) => {
    try {
      const res = await fetch(`${API}/api/cases/stage-notifications/config/${cfg.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (data.ok) { loadData(); showToast(t('common.saved') || 'Saved'); }
    } catch { /* ignore */ }
  };

  const STAGE_COLORS = {
    waiting_for_support: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    clarification_with_recipient: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    solution_proposed: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
    reopened: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    closed: 'bg-gray-100 text-[var(--text-secondary,#4B5563)] dark:bg-gray-800 dark:text-[var(--text-tertiary,#6B7280)]',
  };

  const channelColumns = [
    { key: 'email_customer', label: t('stageNotification.emailCustomer'), icon: Mail, desc: t('stageNotification.emailCustomerDesc') },
    { key: 'email_creator', label: t('stageNotification.emailCreator'), icon: Send, desc: t('stageNotification.emailCreatorDesc') },
    { key: 'notification', label: t('stageNotification.inAppNotification'), icon: Bell, desc: t('stageNotification.inAppNotificationDesc') },
  ];

  if (loading) return <div className="text-sm text-[var(--text-secondary)]">Loading...</div>;

  return (
    <div className={`space-y-6 ${!globalEnabled ? 'opacity-60' : ''}`}>
      {/* Global settings card */}
      <div className="border border-line rounded-card p-6 bg-surface-elevated">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-content">{t('stageNotification.globalToggle')}</h3>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5">{t('stageNotification.globalToggleDesc')}</p>
          </div>
          <ToggleSwitch checked={globalEnabled} onChange={() => updateGlobalSetting('enabled', !globalEnabled)} disabled={saving} />
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-line">
          <div>
            <p className="text-sm text-content">{t('stageNotification.onlyEmailCases')}</p>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5">{t('stageNotification.onlyEmailCasesDesc')}</p>
          </div>
          <ToggleSwitch size="sm" checked={onlyEmailCases} onChange={() => updateGlobalSetting('onlyEmailCases', !onlyEmailCases)} disabled={saving || !globalEnabled} />
        </div>
      </div>

      {/* Matrix table */}
      <div className="border border-line rounded-card bg-surface-elevated overflow-hidden">
        <div className="px-6 py-4 border-b border-line">
          <h3 className="text-base font-semibold text-content">{t('stageNotification.channelMatrix')}</h3>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">{t('stageNotification.channelMatrixDesc')}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-surface">
                <th className="text-left px-6 py-3 text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider w-52">
                  {t('stageNotification.stage')}
                </th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider w-20">
                  {t('stageNotification.active')}
                </th>
                {channelColumns.map(ch => (
                  <th key={ch.key} className="text-center px-4 py-3 w-32">
                    <div className="flex flex-col items-center gap-0.5">
                      <ch.icon size={14} className="text-[var(--text-secondary)]" />
                      <span className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{ch.label}</span>
                    </div>
                  </th>
                ))}
                <th className="text-center px-4 py-3 w-32">
                  <div className="flex flex-col items-center gap-0.5">
                    <Clock size={14} className="text-[var(--text-secondary)]" />
                    <span className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('sla.stageConfig')}</span>
                  </div>
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                  {t('stageNotification.template')}
                </th>
              </tr>
            </thead>
            <tbody>
              {configs.map((cfg, i) => {
                const stageLabel = cfg.stage_label || cfg.to_stage;
                const badgeCls = STAGE_COLORS[cfg.to_stage] || STAGE_COLORS.closed;
                return (
                  <tr key={cfg.id} className={`border-b border-line last:border-b-0 ${!cfg.enabled ? 'opacity-50' : ''} hover:bg-surface transition-colors`}>
                    {/* Stage badge */}
                    <td className="px-6 py-3">
                      <span className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-semibold ${badgeCls}`}>
                        {stageLabel}
                      </span>
                    </td>
                    {/* Enabled toggle */}
                    <td className="text-center px-4 py-3">
                      <div className="flex justify-center">
                        <ToggleSwitch size="sm" checked={cfg.enabled} onChange={() => updateConfig(cfg, { enabled: !cfg.enabled })} disabled={!globalEnabled} />
                      </div>
                    </td>
                    {/* Channel toggles */}
                    {channelColumns.map(ch => (
                      <td key={ch.key} className="text-center px-4 py-3">
                        <div className="flex justify-center">
                          <ToggleSwitch size="sm" checked={cfg[ch.key] === true} onChange={() => updateConfig(cfg, { [ch.key]: !cfg[ch.key] })} disabled={!globalEnabled || !cfg.enabled} />
                        </div>
                      </td>
                    ))}
                    {/* SLA-Timer Verhalten */}
                    <td className="text-center px-4 py-3">
                      <select
                        className="px-2 py-1.5 text-xs rounded-lg border border-line bg-surface text-content focus:outline-none focus:ring-1 focus:ring-primary-500/30 disabled:opacity-40"
                        value={cfg.sla_verhalten || 'laeuft'}
                        onChange={e => updateConfig(cfg, { sla_verhalten: e.target.value })}
                        disabled={!globalEnabled}
                      >
                        <option value="laeuft">● {t('sla.stageLaeuft')}</option>
                        <option value="pausiert">● {t('sla.stagePausiert')}</option>
                        <option value="gestoppt">● {t('sla.stageGestoppt')}</option>
                      </select>
                    </td>
                    {/* Template select */}
                    <td className="px-4 py-3">
                      <select
                        className="w-full px-2 py-1.5 text-xs rounded-lg border border-line bg-surface text-content focus:outline-none focus:ring-1 focus:ring-primary-500/30 disabled:opacity-40"
                        value={cfg.email_template_key || ''}
                        onChange={e => updateConfig(cfg, { email_template_key: e.target.value || null })}
                        disabled={!globalEnabled || !cfg.enabled || (!cfg.email_customer && !cfg.email_creator)}
                      >
                        <option value="">— {t('stageNotification.noTemplate')} —</option>
                        {templates.map(tpl => (
                          <option key={tpl.id} value={tpl.funktion}>{tpl.name}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
              {configs.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-sm text-[var(--text-secondary)]">{t('stageNotification.noRules')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend / Info box */}
      <div className="border border-line rounded-card p-4 bg-surface-elevated">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-[var(--text-secondary)]">
          {channelColumns.map(ch => (
            <div key={ch.key} className="flex items-start gap-2">
              <ch.icon size={13} className="mt-0.5 shrink-0" />
              <span><strong>{ch.label}:</strong> {ch.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* How it works */}
      <div className="border border-line rounded-card p-4 bg-surface-elevated">
        <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
          <strong>{t('stageNotification.howItWorks')}:</strong> {t('stageNotification.howItWorksDesc')}
        </p>
      </div>

      {/* SLA Escalation Config */}
      <SlaEscalationConfig t={t} showToast={showToast} />
    </div>
  );
}

// ─── SLA Escalation Config ───
function SlaEscalationConfig({ t, showToast }) {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/cases/sla-escalation/config`, { credentials: 'include' })
      .then(r => r.json()).then(d => {
        if (d.ok) setConfig({ warning_pct: d.warning_pct, critical_pct: d.critical_pct, warning_notify: d.warning_notify, critical_notify: d.critical_notify, overdue_notify: d.overdue_notify, eskalation_user_ids: d.eskalation_user_ids || [] });
      }).catch(() => {});
    fetch(`${API}/api/users`, { credentials: 'include' })
      .then(r => r.json()).then(d => {
        const list = (d.users || d || []).filter(u => u.aktiv !== false);
        setUsers(list);
      }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/cases/sla-escalation/config`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      const d = await res.json();
      if (d.ok) showToast(t('common.saved') || 'Saved');
    } catch (e) { showToast(e.message); }
    setSaving(false);
  };

  if (!config) return null;

  const NOTIFY_OPTIONS = [
    { value: 'app', label: t('sla.channelApp') },
    { value: 'email', label: t('sla.channelEmail') },
    { value: 'both', label: t('sla.channelBoth') },
  ];

  const levels = [
    { key: 'warning', label: t('sla.warningAt'), pctKey: 'warning_pct', notifyKey: 'warning_notify', color: 'text-amber-500' },
    { key: 'critical', label: t('sla.criticalAt'), pctKey: 'critical_pct', notifyKey: 'critical_notify', color: 'text-red-500' },
    { key: 'overdue', label: t('sla.overdueAt'), pctKey: null, notifyKey: 'overdue_notify', color: 'text-red-600 font-bold' },
  ];

  return (
    <div className="border border-line rounded-2xl p-6 bg-surface-elevated">
      <h3 className="text-base font-semibold text-content mb-4 flex items-center gap-2">
        <Zap size={16} className="text-amber-500" />
        {t('sla.escalation')}
      </h3>
      <div className="space-y-4">
        {levels.map(lv => (
          <div key={lv.key} className="flex items-center gap-4 flex-wrap">
            <span className={`text-sm font-medium w-32 ${lv.color}`}>{lv.label}</span>
            {lv.pctKey ? (
              <div className="flex items-center gap-1.5">
                <input
                  type="number" min={1} max={99}
                  className="w-16 px-2 py-1.5 text-sm rounded-lg border border-line bg-surface text-content text-center focus:outline-none focus:ring-1 focus:ring-primary-500/30"
                  value={config[lv.pctKey] || ''}
                  onChange={e => setConfig(prev => ({ ...prev, [lv.pctKey]: parseInt(e.target.value) || 0 }))}
                />
                <span className="text-xs text-[var(--text-secondary)]">%</span>
              </div>
            ) : (
              <span className="text-sm text-[var(--text-secondary)] w-16 text-center">100%</span>
            )}
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-[var(--text-secondary)]">{t('sla.notifyChannel')}:</span>
              <select
                className="px-2 py-1.5 text-xs rounded-lg border border-line bg-surface text-content focus:outline-none focus:ring-1 focus:ring-primary-500/30"
                value={config[lv.notifyKey] || 'both'}
                onChange={e => setConfig(prev => ({ ...prev, [lv.notifyKey]: e.target.value }))}
              >
                {NOTIFY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
      {/* Eskalation Recipients Multi-Select */}
      <div className="mt-4 pt-4 border-t border-line">
        <label className="text-sm font-medium text-content mb-2 block">
          {t('sla.escalationRecipients') || 'Eskalation an'}
        </label>
        <p className="text-xs text-[var(--text-secondary)] mb-3">
          {t('sla.escalationRecipientsHint') || 'Diese Benutzer erhalten SLA-Eskalations-Benachrichtigungen (zusätzlich zu CS-Teamlead und zugewiesenem Agent).'}
        </p>
        <div className="flex flex-wrap gap-2">
          {users.filter(u => !['portal_kunde', 'portal_mitarbeiter'].includes(u.rolle)).map(u => {
            const selected = (config.eskalation_user_ids || []).includes(u.id);
            return (
              <button key={u.id} type="button"
                onClick={() => {
                  setConfig(prev => {
                    const ids = prev.eskalation_user_ids || [];
                    return { ...prev, eskalation_user_ids: selected ? ids.filter(id => id !== u.id) : [...ids, u.id] };
                  });
                }}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  selected
                    ? 'bg-[#338edc] text-white border-[#338edc]'
                    : 'bg-surface text-content border-line hover:border-[#338edc]/40'
                }`}
              >
                {u.name}{u.rolle ? ` (${u.rolle})` : ''}
              </button>
            );
          })}
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-line flex justify-end">
        <button onClick={save} disabled={saving}
          className="px-4 py-2 text-xs font-semibold rounded-lg bg-[#338edc] text-white hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors">
          {saving ? '...' : t('common.save')}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  CATEGORIES TAB
// ════════════════════════════════════════════════════════════════
function CategoriesTab({ t, showToast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null); // null = list, {} = new, {id} = edit
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = async () => {
    try {
      const d = await fetch(`${API}/api/cases/categories`, { credentials: 'include' }).then(r => r.json());
      setItems(d.categories || []);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const method = editItem.id ? 'PUT' : 'POST';
    const url = editItem.id ? `${API}/api/cases/categories/${editItem.id}` : `${API}/api/cases/categories`;
    try {
      const r = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(editItem) });
      const d = await r.json();
      if (d.error) { showToast(d.error); return; }
      showToast(t('casesConfig.categories.saved'));
      setEditItem(null);
      load();
    } catch (e) { showToast(e.message); }
  };

  const del = (id) => setConfirmDeleteId(id);
  const executeDel = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    if (!id) return;
    try {
      const r = await fetch(`${API}/api/cases/categories/${id}`, { method: 'DELETE', credentials: 'include' });
      const d = await r.json();
      if (d.error) { showToast(d.error); return; }
      showToast(t('casesConfig.categories.deleted'));
      load();
    } catch (e) { showToast(e.message); }
  };

  if (editItem) {
    return (
      <div className="border border-line rounded-card p-6 bg-surface-card space-y-4">
        <h2 className="text-lg font-bold text-content">{editItem.id ? t('common.edit') : t('casesConfig.categories.new')}</h2>
        <div>
          <label className="text-xs font-semibold text-content-tertiary uppercase">{t('casesConfig.categories.name')}</label>
          <input className={inp} value={editItem.name || ''} onChange={e => setEditItem({ ...editItem, name: e.target.value })} />
        </div>
        <div>
          <label className="text-xs font-semibold text-content-tertiary uppercase">{t('casesConfig.categories.color')}</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {FARBE_OPTIONS.map(f => (
              <button key={f.value} onClick={() => setEditItem({ ...editItem, color: f.value })}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${editItem.color === f.value ? 'ring-2 ring-primary-500' : ''} ${f.cls}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-content-tertiary uppercase">{t('casesConfig.categories.sortOrder')}</label>
          <input type="number" className={inp} value={editItem.sort_order || 0} onChange={e => setEditItem({ ...editItem, sort_order: Number(e.target.value) })} />
        </div>
        <div className="flex gap-2">
          <button onClick={save} className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:brightness-110 transition-all"><Save className="w-4 h-4 inline mr-1" />{t('common.save')}</button>
          <button onClick={() => setEditItem(null)} className="px-4 py-2 rounded-lg text-sm font-semibold border border-line text-content hover:bg-surface-hover">{t('common.cancel')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-content">{t('casesConfig.categories.title')}</h2>
          <p className="text-sm text-[var(--text-secondary)]">{t('casesConfig.categories.subtitle')}</p>
        </div>
        <button onClick={() => setEditItem({ name: '', color: 'gray', sort_order: 0 })}
          className="px-3 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:brightness-110 transition-all flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> {t('casesConfig.categories.new')}
        </button>
      </div>
      {loading ? <div className="py-8 text-center text-content-tertiary">Loading...</div> : items.length === 0 ? (
        <div className="py-8 text-center text-content-tertiary border border-dashed border-line rounded-card">{t('casesConfig.categories.noEntries')}</div>
      ) : (
        <div className="border border-line rounded-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-surface-inset text-[10px] font-bold text-content-tertiary uppercase">
                <th className="px-4 py-2 text-left">{t('casesConfig.categories.name')}</th>
                <th className="px-4 py-2 text-left">{t('casesConfig.categories.color')}</th>
                <th className="px-4 py-2 text-right">{t('casesConfig.categories.sortOrder')}</th>
                <th className="px-4 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} className="border-t border-line hover:bg-surface-hover transition-colors">
                  <td className="px-4 py-2.5 text-sm font-medium text-content">{item.name}</td>
                  <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${farbeCls(item.color)}`}>{item.color}</span></td>
                  <td className="px-4 py-2.5 text-sm text-right text-content-tertiary">{item.sort_order}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => setEditItem(item)} className="p-1 text-content-tertiary hover:text-content"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => del(item.id)} className="p-1 text-content-tertiary hover:text-red-500 ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDialog
        open={!!confirmDeleteId}
        onConfirm={executeDel}
        onCancel={() => setConfirmDeleteId(null)}
        title={t('common.deleteConfirmTitle', 'Loeschen')}
        message={t('common.deleteConfirm', 'Wirklich loeschen?')}
        confirmLabel={t('common.delete', 'Loeschen')}
        cancelLabel={t('common.cancel', 'Abbrechen')}
        variant="danger"
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  TAGS TAB
// ════════════════════════════════════════════════════════════════
function TagsTab({ t, showToast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = async () => {
    try {
      const d = await fetch(`${API}/api/cases/tags`, { credentials: 'include' }).then(r => r.json());
      setItems(d.tags || []);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const method = editItem.id ? 'PUT' : 'POST';
    const url = editItem.id ? `${API}/api/cases/tags/${editItem.id}` : `${API}/api/cases/tags`;
    try {
      const r = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(editItem) });
      const d = await r.json();
      if (d.error) { showToast(d.error); return; }
      showToast(t('casesConfig.tags.saved'));
      setEditItem(null);
      load();
    } catch (e) { showToast(e.message); }
  };

  const del = (id) => setConfirmDeleteId(id);
  const executeDel = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    if (!id) return;
    try {
      await fetch(`${API}/api/cases/tags/${id}`, { method: 'DELETE', credentials: 'include' });
      showToast(t('casesConfig.tags.deleted'));
      load();
    } catch (e) { showToast(e.message); }
  };

  if (editItem) {
    return (
      <div className="border border-line rounded-card p-6 bg-surface-card space-y-4">
        <h2 className="text-lg font-bold text-content">{editItem.id ? t('common.edit') : t('casesConfig.tags.new')}</h2>
        <div>
          <label className="text-xs font-semibold text-content-tertiary uppercase">{t('casesConfig.tags.name')}</label>
          <input className={inp} value={editItem.name || ''} onChange={e => setEditItem({ ...editItem, name: e.target.value })} />
        </div>
        <div>
          <label className="text-xs font-semibold text-content-tertiary uppercase">{t('casesConfig.tags.color')}</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {FARBE_OPTIONS.map(f => (
              <button key={f.value} onClick={() => setEditItem({ ...editItem, color: f.value })}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${editItem.color === f.value ? 'ring-2 ring-primary-500' : ''} ${f.cls}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={save} className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:brightness-110 transition-all"><Save className="w-4 h-4 inline mr-1" />{t('common.save')}</button>
          <button onClick={() => setEditItem(null)} className="px-4 py-2 rounded-lg text-sm font-semibold border border-line text-content hover:bg-surface-hover">{t('common.cancel')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-content">{t('casesConfig.tags.title')}</h2>
          <p className="text-sm text-[var(--text-secondary)]">{t('casesConfig.tags.subtitle')}</p>
        </div>
        <button onClick={() => setEditItem({ name: '', color: 'gray' })}
          className="px-3 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:brightness-110 transition-all flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> {t('casesConfig.tags.new')}
        </button>
      </div>
      {loading ? <div className="py-8 text-center text-content-tertiary">Loading...</div> : items.length === 0 ? (
        <div className="py-8 text-center text-content-tertiary border border-dashed border-line rounded-card">{t('casesConfig.tags.noEntries')}</div>
      ) : (
        <div className="border border-line rounded-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-surface-inset text-[10px] font-bold text-content-tertiary uppercase">
                <th className="px-4 py-2 text-left">{t('casesConfig.tags.name')}</th>
                <th className="px-4 py-2 text-left">{t('casesConfig.tags.color')}</th>
                <th className="px-4 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} className="border-t border-line hover:bg-surface-hover transition-colors">
                  <td className="px-4 py-2.5 text-sm font-medium text-content">{item.name}</td>
                  <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${farbeCls(item.color)}`}>{item.color}</span></td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => setEditItem(item)} className="p-1 text-content-tertiary hover:text-content"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => del(item.id)} className="p-1 text-content-tertiary hover:text-red-500 ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDialog
        open={!!confirmDeleteId}
        onConfirm={executeDel}
        onCancel={() => setConfirmDeleteId(null)}
        title={t('common.deleteConfirmTitle', 'Loeschen')}
        message={t('common.deleteConfirm', 'Wirklich loeschen?')}
        confirmLabel={t('common.delete', 'Loeschen')}
        cancelLabel={t('common.cancel', 'Abbrechen')}
        variant="danger"
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  TEMPLATES TAB
// ════════════════════════════════════════════════════════════════
function TemplatesTab({ t, showToast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = async () => {
    try {
      const d = await fetch(`${API}/api/cases/reply-templates`, { credentials: 'include' }).then(r => r.json());
      setItems(d.templates || []);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const method = editItem.id ? 'PUT' : 'POST';
    const url = editItem.id ? `${API}/api/cases/reply-templates/${editItem.id}` : `${API}/api/cases/reply-templates`;
    try {
      const r = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(editItem) });
      const d = await r.json();
      if (d.error) { showToast(d.error); return; }
      showToast(t('casesConfig.templates.saved'));
      setEditItem(null);
      load();
    } catch (e) { showToast(e.message); }
  };

  const del = (id) => setConfirmDeleteId(id);
  const executeDel = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    if (!id) return;
    try {
      await fetch(`${API}/api/cases/reply-templates/${id}`, { method: 'DELETE', credentials: 'include' });
      showToast(t('casesConfig.templates.deleted'));
      load();
    } catch (e) { showToast(e.message); }
  };

  if (editItem) {
    return (
      <div className="border border-line rounded-card p-6 bg-surface-card space-y-4">
        <h2 className="text-lg font-bold text-content">{editItem.id ? t('common.edit') : t('casesConfig.templates.new')}</h2>
        <div>
          <label className="text-xs font-semibold text-content-tertiary uppercase">{t('casesConfig.templates.titleField')}</label>
          <input className={inp} value={editItem.title || ''} onChange={e => setEditItem({ ...editItem, title: e.target.value })} />
        </div>
        <div>
          <label className="text-xs font-semibold text-content-tertiary uppercase">{t('casesConfig.templates.category')}</label>
          <input className={inp} value={editItem.category || ''} onChange={e => setEditItem({ ...editItem, category: e.target.value })} placeholder="z.B. Allgemein, Versand, Retoure..." />
        </div>
        <div>
          <label className="text-xs font-semibold text-content-tertiary uppercase">{t('casesConfig.templates.content')}</label>
          <textarea className={`${inp} min-h-[200px]`} value={editItem.content_html || ''} onChange={e => setEditItem({ ...editItem, content_html: e.target.value })} />
          <p className="text-[10px] text-content-tertiary mt-1">{t('casesConfig.templates.variables')}</p>
        </div>
        {editItem.content_html && (
          <div>
            <label className="text-xs font-semibold text-content-tertiary uppercase mb-1 block">{t('casesConfig.templates.preview')}</label>
            <div className="border border-line rounded-lg p-3 bg-surface-inset text-sm text-content prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: editItem.content_html }} />
          </div>
        )}
        <div className="flex gap-2">
          <button onClick={save} className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:brightness-110 transition-all"><Save className="w-4 h-4 inline mr-1" />{t('common.save')}</button>
          <button onClick={() => setEditItem(null)} className="px-4 py-2 rounded-lg text-sm font-semibold border border-line text-content hover:bg-surface-hover">{t('common.cancel')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-content">{t('casesConfig.templates.title')}</h2>
          <p className="text-sm text-[var(--text-secondary)]">{t('casesConfig.templates.subtitle')}</p>
        </div>
        <button onClick={() => setEditItem({ title: '', content_html: '', category: '', sort_order: 0 })}
          className="px-3 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:brightness-110 transition-all flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> {t('casesConfig.templates.new')}
        </button>
      </div>
      {loading ? <div className="py-8 text-center text-content-tertiary">Loading...</div> : items.length === 0 ? (
        <div className="py-8 text-center text-content-tertiary border border-dashed border-line rounded-card">{t('casesConfig.templates.noEntries')}</div>
      ) : (
        <div className="border border-line rounded-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-surface-inset text-[10px] font-bold text-content-tertiary uppercase">
                <th className="px-4 py-2 text-left">{t('casesConfig.templates.titleField')}</th>
                <th className="px-4 py-2 text-left">{t('casesConfig.templates.category')}</th>
                <th className="px-4 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} className="border-t border-line hover:bg-surface-hover transition-colors">
                  <td className="px-4 py-2.5 text-sm font-medium text-content">{item.title}</td>
                  <td className="px-4 py-2.5 text-sm text-content-tertiary">{item.category || '–'}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => setEditItem(item)} className="p-1 text-content-tertiary hover:text-content"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => del(item.id)} className="p-1 text-content-tertiary hover:text-red-500 ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDeleteId}
        onCancel={() => setConfirmDeleteId(null)}
        onConfirm={executeDel}
        title={t('common.deleteConfirmTitle', 'Loeschen')}
        message={t('common.deleteConfirm', 'Wirklich loeschen?')}
        confirmLabel={t('common.delete', 'Loeschen')}
        cancelLabel={t('common.cancel', 'Abbrechen')}
        variant="danger"
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Autogen-Whitelist Tab (#1442) — email_autogen_whitelist + email_autogen_filtered
   ═══════════════════════════════════════════════════════════ */
function AutogenTab({ t, showToast }) {
  const [whitelist, setWhitelist] = useState([]);
  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [newKeyTyp, setNewKeyTyp] = useState('from_domain');
  const [adding, setAdding] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmWhitelistRow, setConfirmWhitelistRow] = useState(null);
  const limit = 50;

  const fmtDt = (d) => d ? new Date(d).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const loadWhitelist = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/inbound-emails/autogen-whitelist`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setWhitelist(d.entries || []); }
    } catch { /* ignore */ }
  }, []);

  const loadEmails = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search) params.set('search', search);
      const res = await fetch(`${API}/api/inbound-emails/autogen-filtered?${params}`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setEmails(d.emails || []); setTotal(d.total || 0); }
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, search]);

  useEffect(() => { loadWhitelist(); }, [loadWhitelist]);
  useEffect(() => { loadEmails(); }, [loadEmails]);

  useEffect(() => {
    const tm = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(tm);
  }, [searchInput]);

  const addDomain = async (domain, keyTyp) => {
    const dom = (domain || '').trim().toLowerCase();
    if (!dom) return;
    setAdding(true);
    try {
      const res = await fetch(`${API}/api/inbound-emails/autogen-whitelist`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: dom, key_typ: keyTyp || 'from_domain' }),
      });
      if (res.ok) {
        showToast(t('autogenWhitelist.added', 'Domain zur Whitelist hinzugefuegt'));
        setNewDomain('');
        await Promise.all([loadWhitelist(), loadEmails()]);
      } else {
        const err = await res.json().catch(() => ({}));
        showToast(err.error || t('autogenWhitelist.addError', 'Fehler beim Hinzufuegen'));
      }
    } catch {
      showToast(t('autogenWhitelist.addError', 'Fehler beim Hinzufuegen'));
    }
    setAdding(false);
  };

  const toggleAktiv = async (entry) => {
    try {
      const res = await fetch(`${API}/api/inbound-emails/autogen-whitelist/${entry.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aktiv: !entry.aktiv }),
      });
      if (res.ok) { await Promise.all([loadWhitelist(), loadEmails()]); }
    } catch { /* ignore */ }
  };

  const executeDelete = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      const res = await fetch(`${API}/api/inbound-emails/autogen-whitelist/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        showToast(t('autogenWhitelist.deleted', 'Whitelist-Eintrag geloescht'));
        await Promise.all([loadWhitelist(), loadEmails()]);
      }
    } catch { /* ignore */ }
  };

  const executeWhitelistFromRow = async () => {
    const row = confirmWhitelistRow;
    setConfirmWhitelistRow(null);
    if (row) await addDomain(row.from_domain, 'from_domain');
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-6">
      {/* ── Whitelist ── */}
      <div className="bg-surface-elevated border border-line rounded-card p-5">
        <h2 className="text-sm font-bold text-content mb-1">{t('autogenWhitelist.whitelistTitle', 'Whitelist')}</h2>
        <p className="text-xs text-[var(--text-tertiary)] mb-4">{t('autogenWhitelist.whitelistHint', 'Auto-generierte Mails erzeugen nur dann Cases, wenn ihre Domain hier eingetragen und aktiv ist.')}</p>

        {/* Add form */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <input
            className={inp + ' max-w-xs'}
            placeholder={t('autogenWhitelist.domainPlaceholder', 'z.B. eet.energy')}
            value={newDomain}
            onChange={e => setNewDomain(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addDomain(newDomain, newKeyTyp); }}
          />
          <select className={inp + ' max-w-[180px]'} value={newKeyTyp} onChange={e => setNewKeyTyp(e.target.value)}>
            <option value="from_domain">{t('autogenWhitelist.keyFromDomain', 'Absender-Domain')}</option>
            <option value="customer_domain">{t('autogenWhitelist.keyCustomerDomain', 'Kunden-Domain')}</option>
          </select>
          <button
            onClick={() => addDomain(newDomain, newKeyTyp)}
            disabled={adding || !newDomain.trim()}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" />
            {t('autogenWhitelist.add', 'Hinzufuegen')}
          </button>
        </div>

        {whitelist.length === 0 ? (
          <p className="text-sm text-[var(--text-tertiary)]">{t('autogenWhitelist.empty', 'Keine Eintraege.')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[var(--text-tertiary)] border-b border-line">
                  <th className="py-2 pr-4 font-semibold">{t('autogenWhitelist.colDomain', 'Domain')}</th>
                  <th className="py-2 pr-4 font-semibold">{t('autogenWhitelist.colKeyTyp', 'Typ')}</th>
                  <th className="py-2 pr-4 font-semibold">{t('autogenWhitelist.colAddedBy', 'Hinzugefuegt von')}</th>
                  <th className="py-2 pr-4 font-semibold">{t('autogenWhitelist.colAddedAt', 'Am')}</th>
                  <th className="py-2 pr-4 font-semibold">{t('autogenWhitelist.colAktiv', 'Aktiv')}</th>
                  <th className="py-2 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {whitelist.map(w => (
                  <tr key={w.id} className="border-b border-line last:border-0">
                    <td className="py-2 pr-4 font-medium text-content">{w.domain}</td>
                    <td className="py-2 pr-4 text-[var(--text-secondary)]">
                      {w.key_typ === 'customer_domain'
                        ? t('autogenWhitelist.keyCustomerDomain', 'Kunden-Domain')
                        : t('autogenWhitelist.keyFromDomain', 'Absender-Domain')}
                    </td>
                    <td className="py-2 pr-4 text-[var(--text-secondary)]">{w.hinzugefuegt_von_name || '—'}</td>
                    <td className="py-2 pr-4 text-[var(--text-secondary)]">{fmtDt(w.hinzugefuegt_am)}</td>
                    <td className="py-2 pr-4">
                      <ToggleSwitch checked={!!w.aktiv} onChange={() => toggleAktiv(w)} />
                    </td>
                    <td className="py-2 text-right">
                      <button
                        onClick={() => setConfirmDeleteId(w.id)}
                        className="p-1.5 rounded-lg text-[var(--text-tertiary)] hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        title={t('common.delete', 'Loeschen')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Gefilterte Mails ── */}
      <div className="bg-surface-elevated border border-line rounded-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <h2 className="text-sm font-bold text-content">{t('autogenWhitelist.filteredTitle', 'Nicht umgewandelte Auto-Mails')}</h2>
          <span className="text-xs text-[var(--text-tertiary)]">{t('autogenWhitelist.totalCount', '{count} Eintraege').replace('{count}', String(total))}</span>
        </div>
        <p className="text-xs text-[var(--text-tertiary)] mb-4">{t('autogenWhitelist.filteredHint', 'Whitelisten wirkt nur fuer zukuenftige Mails — bereits gefilterte Mails werden nicht automatisch nachverarbeitet.')}</p>

        <input
          className={inp + ' max-w-sm mb-4'}
          placeholder={t('autogenWhitelist.searchPlaceholder', 'Suche Absender / Domain / Betreff ...')}
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
        />

        {loading ? (
          <p className="text-sm text-[var(--text-tertiary)]">{t('common.loading', 'Laedt ...')}</p>
        ) : emails.length === 0 ? (
          <p className="text-sm text-[var(--text-tertiary)]">{t('autogenWhitelist.noFiltered', 'Keine gefilterten Mails.')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[var(--text-tertiary)] border-b border-line">
                  <th className="py-2 pr-4 font-semibold">{t('autogenWhitelist.colDate', 'Datum')}</th>
                  <th className="py-2 pr-4 font-semibold">{t('autogenWhitelist.colFrom', 'Absender')}</th>
                  <th className="py-2 pr-4 font-semibold">{t('autogenWhitelist.colFromDomain', 'From-Domain')}</th>
                  <th className="py-2 pr-4 font-semibold">{t('autogenWhitelist.colKunde', 'Erkannter Kunde')}</th>
                  <th className="py-2 pr-4 font-semibold">{t('autogenWhitelist.colBetreff', 'Betreff')}</th>
                  <th className="py-2 pr-4 font-semibold">{t('autogenWhitelist.colGrund', 'Grund')}</th>
                  <th className="py-2 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {emails.map(m => (
                  <tr key={m.id} className={`border-b border-line last:border-0 ${m.whitelisted ? 'opacity-50' : ''}`}>
                    <td className="py-2 pr-4 whitespace-nowrap text-[var(--text-secondary)]">{fmtDt(m.empfangen_am)}</td>
                    <td className="py-2 pr-4 text-content">{m.from_addr || '—'}</td>
                    <td className="py-2 pr-4 font-medium text-content">{m.from_domain || '—'}</td>
                    <td className="py-2 pr-4 text-[var(--text-secondary)]">{m.kunde_name || '—'}</td>
                    <td className="py-2 pr-4 text-[var(--text-secondary)] max-w-[320px] truncate" title={m.betreff || ''}>{m.betreff || '—'}</td>
                    <td className="py-2 pr-4 text-xs text-[var(--text-tertiary)]">{m.grund}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {m.whitelisted ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          <CheckCircle className="w-3 h-3" />
                          {t('autogenWhitelist.alreadyWhitelisted', 'Gewhitelistet')}
                        </span>
                      ) : m.from_domain ? (
                        <button
                          onClick={() => setConfirmWhitelistRow(m)}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-line text-content hover:bg-surface-inset ml-auto"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          {t('autogenWhitelist.whitelistBtn', 'Domain whitelisten')}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-line text-content disabled:opacity-40 hover:bg-surface-inset"
            >
              {t('common.prev', 'Zurueck')}
            </button>
            <span className="text-xs text-[var(--text-tertiary)]">{page} / {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-line text-content disabled:opacity-40 hover:bg-surface-inset"
            >
              {t('common.next', 'Weiter')}
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmDeleteId}
        onConfirm={executeDelete}
        onCancel={() => setConfirmDeleteId(null)}
        title={t('autogenWhitelist.deleteTitle', 'Whitelist-Eintrag loeschen')}
        message={t('autogenWhitelist.deleteMsg', 'Auto-Mails dieser Domain erzeugen danach keine Cases mehr. Wirklich loeschen?')}
        confirmLabel={t('common.delete', 'Loeschen')}
        cancelLabel={t('common.cancel', 'Abbrechen')}
        variant="danger"
      />
      <ConfirmDialog
        open={!!confirmWhitelistRow}
        onConfirm={executeWhitelistFromRow}
        onCancel={() => setConfirmWhitelistRow(null)}
        title={t('autogenWhitelist.confirmTitle', 'Domain whitelisten')}
        message={t('autogenWhitelist.confirmMsg', 'Zukuenftige auto-generierte Mails von "{domain}" erzeugen dann Cases. Bereits gefilterte Mails werden NICHT nachverarbeitet.').replace('{domain}', confirmWhitelistRow?.from_domain || '')}
        confirmLabel={t('autogenWhitelist.whitelistBtn', 'Domain whitelisten')}
        cancelLabel={t('common.cancel', 'Abbrechen')}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Tab: Daily Cases Analyse (#1853)
   ═══════════════════════════════════════════════════════════ */

/** Kanal je Empfänger sichtbar machen (#1819-Muster) — sonst sendet man ins Leere. */
function KanalBadge({ kanal, t }) {
  const map = {
    telegram: ['bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', t('casesConfig.analyse.kanalTelegram', 'Telegram')],
    sms: ['bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', t('casesConfig.analyse.kanalSms', 'SMS')],
    email: ['bg-gray-100 text-[var(--text-secondary)] dark:bg-gray-800', t('casesConfig.analyse.kanalEmail', 'E-Mail')],
    kein_kanal: ['bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', t('casesConfig.analyse.kanalKeiner', 'kein Kanal')],
  };
  const [cls, label] = map[kanal] || map.kein_kanal;
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>{label}</span>;
}

const WOCHENTAGE = [
  { v: 1, k: 'mon' }, { v: 2, k: 'tue' }, { v: 3, k: 'wed' }, { v: 4, k: 'thu' },
  { v: 5, k: 'fri' }, { v: 6, k: 'sat' }, { v: 0, k: 'sun' },
];

/**
 * Mehrfachauswahl-Kachel. Der aktive Zustand muss auf einen Blick erkennbar sein
 * (weiss + Häkchen auf abgesetztem Grund, Design-System §Toggle Group) — vorher
 * stand Weiss auf einer weissen Card und man hat gewählte Tage wieder abgewählt,
 * ohne es zu merken.
 */
function AuswahlChip({ aktiv, onClick, disabled, breit, children }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`flex items-center justify-center gap-1 ${breit ? 'px-3' : 'w-11'} py-2 rounded-lg text-xs transition-colors ${
        aktiv
          ? 'bg-white dark:bg-surface-elevated text-content font-semibold shadow-sm'
          : 'text-[var(--text-secondary)] font-medium hover:text-content'}`}>
      {aktiv && <Check className="w-3 h-3 shrink-0" />}
      {children}
    </button>
  );
}

/** Gewählte Tage als Klartext, zusammenhängende Blöcke als „Mo–Fr". */
function tageText(werte, t) {
  const gewaehlt = WOCHENTAGE.map((w, i) => ({ ...w, i })).filter(w => (werte || []).includes(w.v));
  if (!gewaehlt.length) return '';
  if (gewaehlt.length === 7) return t('casesConfig.analyse.tageAlle', 'täglich');
  const label = (k) => t(`casesConfig.analyse.tag_${k}`, k);
  const teile = [];
  for (let i = 0; i < gewaehlt.length;) {
    let j = i;
    while (j + 1 < gewaehlt.length && gewaehlt[j + 1].i === gewaehlt[j].i + 1) j++;
    teile.push(j - i >= 2
      ? `${label(gewaehlt[i].k)}–${label(gewaehlt[j].k)}`
      : gewaehlt.slice(i, j + 1).map(x => label(x.k)).join(', '));
    i = j + 1;
  }
  return teile.join(', ');
}

function AnalyseTab({ t, showToast }) {
  const [cfg, setCfg] = useState(null);
  const [meta, setMeta] = useState({ users: [], stages: [], kategorien: [], cron: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testLaeuft, setTestLaeuft] = useState(false);
  const [letzter, setLetzter] = useState(null);

  const laden = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/cases/analyse/config`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setCfg(d.config); setMeta({ users: d.users || [], stages: d.stages || [], kategorien: d.kategorien || [], cron: d.cron }); })
      .catch(() => showToast(t('common.error', 'Fehler')))
      .finally(() => setLoading(false));
    fetch(`${API}/api/cases/analyse/letzter-lauf`, { credentials: 'include' })
      .then(r => r.json()).then(setLetzter).catch(() => {});
  }, [showToast, t]);

  useEffect(() => { laden(); }, [laden]);

  const speichern = async (patch) => {
    const neu = { ...cfg, ...patch };
    setCfg(neu);
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/cases/analyse/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(patch),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'save_failed');
      setMeta(m => ({ ...m, cron: d.cron }));
      showToast(t('sla.saved', 'Gespeichert'));
    } catch (e) {
      showToast(String(e.message));
      laden();
    }
    setSaving(false);
  };

  const umschalten = (feld, wert) => {
    const liste = cfg[feld] || [];
    const neu = liste.includes(wert) ? liste.filter(x => x !== wert) : [...liste, wert];
    speichern({ [feld]: neu });
  };

  const testlauf = async () => {
    setTestLaeuft(true);
    try {
      const res = await fetch(`${API}/api/cases/analyse/testlauf`, { method: 'POST', credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'test_failed');
      showToast(t('casesConfig.analyse.testGesendet', 'Testlauf gesendet: {n} Fälle geprüft').replace('{n}', d.faelle_geprueft));
      setLetzter({ lauf: null });
      fetch(`${API}/api/cases/analyse/letzter-lauf`, { credentials: 'include' })
        .then(r => r.json()).then(setLetzter).catch(() => {});
    } catch (e) { showToast(String(e.message)); }
    setTestLaeuft(false);
  };

  if (loading || !cfg) return <div className="flex items-center justify-center h-48"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>;

  const lauf = letzter?.lauf;
  const tageWorte = tageText(cfg.wochentage, t);
  const gewaehlteStages = meta.stages.filter(s => (cfg.stages || []).includes(s.key));

  return (
    <div className="space-y-6">
      {/* Master-Switch + Zeitplan */}
      <div className="bg-surface-elevated rounded-card border border-line">
        <div className="px-5 py-3 border-b border-line flex items-center justify-between">
          <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
            <Zap className="w-4 h-4" /> {t('casesConfig.analyse.title', 'Daily Cases Analyse')}
          </h2>
          <ToggleSwitch checked={!!cfg.enabled} onChange={() => speichern({ enabled: !cfg.enabled })} disabled={saving} />
        </div>
        <div className="px-5 py-4 space-y-4">
          <p className="text-xs text-[var(--text-secondary)]">
            {t('casesConfig.analyse.hint', 'Einmal täglich prüft FlowAI die offenen Fälle inhaltlich und meldet Auffälligkeiten als eine Gruppen-Nachricht. Die Analyse schlägt vor — sie ändert keinen Status.')}
          </p>
          <div className="flex flex-wrap items-end gap-6">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('casesConfig.analyse.zeit', 'Sendezeit (Wien)')}</label>
              <input type="time" value={cfg.zeit} onChange={e => setCfg(p => ({ ...p, zeit: e.target.value }))}
                onBlur={() => speichern({ zeit: cfg.zeit })} className={`w-32 ${inp}`} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('casesConfig.analyse.wochentage', 'Wochentage')}</label>
              <div className="inline-flex gap-1 bg-surface-inset border border-line rounded-card p-1">
                {WOCHENTAGE.map(w => (
                  <AuswahlChip key={w.v} aktiv={(cfg.wochentage || []).includes(w.v)}
                    onClick={() => umschalten('wochentage', w.v)} disabled={saving}>
                    {t(`casesConfig.analyse.tag_${w.k}`, w.k)}
                  </AuswahlChip>
                ))}
              </div>
            </div>
          </div>
          <div className="text-xs text-content">
            {tageWorte
              ? t('casesConfig.analyse.sendetText', 'Sendet {tage} um {zeit} Uhr (Wien).')
                  .replace('{tage}', tageWorte).replace('{zeit}', cfg.zeit)
              : t('casesConfig.analyse.keineTage', 'Kein Wochentag gewählt — die Analyse läuft nicht.')}
            <span className="ml-2 text-[var(--text-tertiary,#6B7280)]">
              {t('casesConfig.analyse.cron', 'Zeitplan')}: <code>{meta.cron}</code>
            </span>
          </div>
        </div>
      </div>

      {/* Geprüfte Status */}
      <div className="bg-surface-elevated rounded-card border border-line">
        <div className="px-5 py-3 border-b border-line">
          <h2 className="text-sm font-semibold text-content">{t('casesConfig.analyse.statusTitle', 'Geprüfte Status')}</h2>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="inline-flex flex-wrap gap-1 bg-surface-inset border border-line rounded-card p-1">
            {meta.stages.map(s => (
              <AuswahlChip key={s.key} breit aktiv={(cfg.stages || []).includes(s.key)}
                onClick={() => umschalten('stages', s.key)} disabled={saving}>
                {s.label}
              </AuswahlChip>
            ))}
          </div>
          <div className="text-xs text-content">
            {gewaehlteStages.length
              ? t('casesConfig.analyse.statusText', 'Geprüft werden {n} von {m} Status: {liste}')
                  .replace('{n}', gewaehlteStages.length).replace('{m}', meta.stages.length)
                  .replace('{liste}', gewaehlteStages.map(s => s.label).join(', '))
              : t('casesConfig.analyse.keinStatus', 'Kein Status gewählt — es wird nichts geprüft.')}
          </div>
        </div>
      </div>

      {/* Kategorien + Schwellen */}
      <div className="bg-surface-elevated rounded-card border border-line">
        <div className="px-5 py-3 border-b border-line">
          <h2 className="text-sm font-semibold text-content">{t('casesConfig.analyse.katTitle', 'Kategorien')}</h2>
        </div>
        <div className="px-5 divide-y divide-line">
          {meta.kategorien.map(k => (
            <div key={k.key} className="flex items-center justify-between gap-6 py-3">
              <div className="text-sm text-content">{k.icon} {t(`casesConfig.analyse.kat_${k.key}`, k.label)}</div>
              <ToggleSwitch checked={(cfg.kategorien || []).includes(k.key)} onChange={() => umschalten('kategorien', k.key)} disabled={saving} />
            </div>
          ))}
        </div>
        <div className="px-5 py-4 border-t border-line grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            ['schwelle_antwort_stunden', t('casesConfig.analyse.schwelleAntwort', 'Antwort offen ab (Stunden)'), 1, 720],
            ['schwelle_verwaist_tage', t('casesConfig.analyse.schwelleVerwaist', 'Verwaist ab (Tage)'), 1, 365],
            ['schwelle_pingpong', t('casesConfig.analyse.schwellePingpong', 'Ping-Pong ab (Wechsel)'), 2, 100],
            ['schwelle_sla_prozent', t('casesConfig.analyse.schwelleSla', 'SLA-Risiko ab (%)'), 1, 500],
            ['nachrichten_n', t('casesConfig.analyse.nachrichtenN', 'Geprüfte Nachrichten je Fall'), 1, 20],
          ].map(([feld, label, min, max]) => (
            <div key={feld}>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{label}</label>
              <input type="number" min={min} max={max} value={cfg[feld]}
                onChange={e => setCfg(p => ({ ...p, [feld]: Number(e.target.value) }))}
                onBlur={() => speichern({ [feld]: cfg[feld] })} className={inp} />
            </div>
          ))}
        </div>
      </div>

      {/* Empfänger */}
      <div className="bg-surface-elevated rounded-card border border-line">
        <div className="px-5 py-3 border-b border-line">
          <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
            <Users className="w-4 h-4" /> {t('casesConfig.analyse.empfaenger', 'Empfänger')}
          </h2>
        </div>
        <div className="px-5 pt-3 text-xs text-[var(--text-secondary)]">
          {t('casesConfig.analyse.empfaengerHint', 'Die Nachricht nennt Kundennamen und Wartezeiten. Zur Auswahl stehen deshalb nur Benutzer mit Cases-Berechtigung; wer keinen erreichbaren Kanal hat, ist nicht wählbar.')}
        </div>
        <div className="px-5 py-2 divide-y divide-line max-h-80 overflow-y-auto">
          {meta.users.map(u => (
            <label key={u.id} className={`flex items-center justify-between gap-3 py-2.5 ${u.erreichbar ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
              <span className="flex items-center gap-2.5">
                <input type="checkbox" checked={(cfg.recipients || []).includes(u.id)}
                  onChange={() => umschalten('recipients', u.id)} disabled={saving || !u.erreichbar}
                  className="w-4 h-4 rounded border-line accent-[#338edc]" />
                <span className="text-sm text-content">{u.name}</span>
              </span>
              <KanalBadge kanal={u.kanal} t={t} />
            </label>
          ))}
          {!meta.users.length && (
            <div className="py-3 text-xs text-[var(--text-secondary)]">
              {t('casesConfig.analyse.keineEmpfaenger', 'Kein Benutzer mit Cases-Berechtigung gefunden.')}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-line flex items-center gap-3">
          <button onClick={testlauf} disabled={testLaeuft}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white disabled:opacity-50">
            <Send className="w-3.5 h-3.5" />
            {testLaeuft ? t('casesConfig.analyse.testLaeuft', 'Testlauf läuft …') : t('casesConfig.analyse.testBtn', 'Jetzt senden (nur an mich)')}
          </button>
          <span className="text-xs text-[var(--text-secondary)]">
            {t('casesConfig.analyse.testHint', 'Geht ausschließlich an dich — die Empfängerliste bleibt unberührt.')}
          </span>
        </div>
      </div>

      {/* Letzter Lauf */}
      {lauf && (
        <div className="bg-surface-elevated rounded-card border border-line">
          <div className="px-5 py-3 border-b border-line flex items-center justify-between">
            <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
              <Clock className="w-4 h-4" /> {t('casesConfig.analyse.letzterLauf', 'Letzter Lauf')}
            </h2>
            <button onClick={laden} className="text-[var(--text-secondary)] hover:text-content"><RefreshCw className="w-3.5 h-3.5" /></button>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[var(--text-secondary)]">
              <span>{new Date(lauf.gestartet_am).toLocaleString('de-AT')}</span>
              <span>{lauf.modus === 'test' ? t('casesConfig.analyse.modusTest', 'Testlauf') : t('casesConfig.analyse.modusCron', 'Cronjob')}</span>
              <span className={lauf.status === 'error' ? 'text-red-600 font-semibold' : ''}>{lauf.status}</span>
              <span>{t('casesConfig.analyse.gepruefte', 'Fälle geprüft')}: <b className="text-content">{lauf.faelle_geprueft}</b></span>
              <span>{t('casesConfig.analyse.kiFaelle', 'davon per KI')}: <b className="text-content">{lauf.faelle_ki}</b></span>
              <span>{t('casesConfig.analyse.tokens', 'Tokens')}: <b className="text-content">{lauf.tokens_in} / {lauf.tokens_out}</b>{lauf.modell ? ` (${lauf.modell})` : ''}</span>
              {lauf.oms_verfuegbar === false && <span className="text-amber-600">{t('casesConfig.analyse.omsWeg', 'OMS nicht erreichbar')}</span>}
            </div>
            {lauf.fehler && <div className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{lauf.fehler}</div>}
            {lauf.nachricht && (
              <pre className="text-xs text-content bg-surface-inset border border-line rounded-lg p-3 whitespace-pre-wrap max-h-80 overflow-y-auto">
                {lauf.nachricht.replace(/<a href="[^"]*">([^<]*)<\/a>/g, '$1').replace(/<[^>]+>/g, '')}
              </pre>
            )}
            {!!(letzter?.befunde || []).length && (
              <details className="text-xs">
                <summary className="cursor-pointer text-[var(--text-secondary)]">
                  {t('casesConfig.analyse.befunde', 'Befunde mit Begründung')} ({letzter.befunde.length})
                </summary>
                <div className="mt-2 divide-y divide-line">
                  {letzter.befunde.map((b, i) => (
                    <div key={i} className="py-2">
                      <div className="text-content font-medium">
                        [{b.kategorie}] #{b.case_id} ({b.quelle}) — {b.titel}
                        <span className="ml-2 text-[var(--text-tertiary,#6B7280)] font-normal">{b.herkunft}</span>
                      </div>
                      <div className="text-[var(--text-secondary)]">{b.begruendung}</div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CasesKonfiguration() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useTabParam('sla');
  const [toast, setToast] = useState(null);

  // Stabil halten: ein bei jedem Render neu erzeugtes showToast wandert als
  // Dependency in die laden()-Callbacks der Tabs — dann lädt der Tab bei jedem
  // Toast neu (und noch einmal, wenn der Toast nach 3 s von selbst schliesst)
  // und verwirft dabei ungespeicherte Eingaben.
  const showToast = useCallback((msg) => setToast({ message: msg }), []);
  const closeToast = useCallback(() => setToast(null), []);

  const TABS = TAB_DEFS.map(td => ({
    ...td,
    label: t(td.labelKey),
  }));

  return (
    <div className="px-6 lg:px-8 py-6">
      {/* Header */}
      <h1 className="text-2xl font-extrabold text-content mb-1">{t('casesConfig.title')}</h1>
      <p className="text-sm text-[var(--text-secondary)] mb-6">{t('casesConfig.omsTab')} / {t('casesConfig.slaTab')} / {t('casesConfig.remindersTab')}</p>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-surface-inset border border-line rounded-card p-1 overflow-x-auto scrollbar-hide">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-white dark:bg-surface-elevated text-content shadow-sm'
                : 'text-[var(--text-secondary)] hover:text-content'
            }`}
          >
            <tab.Icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'sla' && <SlaTab t={t} showToast={showToast} />}
      {activeTab === 'categories' && <CategoriesTab t={t} showToast={showToast} />}
      {activeTab === 'tags' && <TagsTab t={t} showToast={showToast} />}
      {activeTab === 'templates' && <TemplatesTab t={t} showToast={showToast} />}
      {activeTab === 'reminders' && <RemindersTab t={t} showToast={showToast} />}
      {activeTab === 'reopen' && <ReopenTab t={t} showToast={showToast} />}
      {activeTab === 'vertretungen' && <VertretungenTab t={t} showToast={showToast} />}
      {activeTab === 'oms' && <OmsTab t={t} showToast={showToast} />}
      {activeTab === 'email_intake' && <EmailIntakeTab t={t} showToast={showToast} />}
      {activeTab === 'autogen' && <AutogenTab t={t} showToast={showToast} />}
      {activeTab === 'notifications' && <StageNotificationTab t={t} showToast={showToast} />}
      {activeTab === 'analyse' && <AnalyseTab t={t} showToast={showToast} />}

      {toast && <Toast message={toast.message} onClose={closeToast} />}
    </div>
  );
}
